/**
 * ApiDocsPage — In-dashboard API Documentation & Integration Guide.
 *
 * Comprehensive reference covering all External API endpoints:
 *   - API Key Management (7 endpoints — dashboard)
 *   - Contacts (9 endpoints)
 *   - Addressbooks (7 endpoints)
 *   - WhatsApp Templates (7 endpoints + 3-step upload)
 *   - WhatsApp Campaigns (15 endpoints)
 *   - Email Templates (11 endpoints)
 *   - Email Campaigns (15 endpoints)
 *   - Error Codes & Scopes Reference
 *
 * Route: /dashboard/api-docs
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useOnboarding } from '../../../../../context/onboardingContext';
import { Skeleton } from 'antd';
import ApiPlanGate from './ApiPlanGate';
import {
  Card,
  Typography,
  Table,
  Button,
  Tag,
  Alert,
  Divider,
} from 'antd';
import {
  BookOutlined,
  CopyOutlined,
  CheckOutlined,
  TeamOutlined,
  FileTextOutlined,
  SendOutlined,
  MailOutlined,
  WarningOutlined,
  ContainerOutlined,
  SafetyOutlined,
  CloudUploadOutlined,
  KeyOutlined,
  ApiOutlined,
  MenuOutlined,
  CloseOutlined,
  RightOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import './ApiKeys.css';

const { Title, Text, Paragraph } = Typography;

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.messageos.io';
const EXTERNAL_BASE = `${BASE_URL}/api/v1/external`;

/* ── Utility helpers ──────────────────────────────────────── */

const copyToClipboard = (text) => {
  const str = typeof text === 'string' ? text : JSON.stringify(text, null, 2);
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(str).then(() => true, () => false);
  }
  const textarea = document.createElement('textarea');
  textarea.value = str;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try { document.execCommand('copy'); ok = true; } catch {}
  document.body.removeChild(textarea);
  return Promise.resolve(ok);
};

const CodeBlock = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard(children).then((ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };
  return (
    <div className="code-block">
      <Button
        className="copy-btn"
        size="small"
        type="text"
        icon={copied ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined style={{ color: '#fff' }} />}
        onClick={handleCopy}
      />
      <pre>{children}</pre>
    </div>
  );
};

const MethodBadge = ({ method }) => (
  <span className={`method-badge ${method.toLowerCase()}`}>{method}</span>
);

/* ── Copy Integration Prompt Button ─────────────────────────── */

const CopyPromptButton = ({ promptKey, label, isEnterprise }) => {
  const [copied, setCopied] = useState(false);
  const prompt = UI_PROMPTS[promptKey];
  if (!prompt || !isEnterprise) return null;
  const handleCopy = () => {
    copyToClipboard(prompt).then((ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };
  return (
    <Button
      className="copy-prompt-btn"
      icon={copied ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CodeOutlined />}
      size="small"
      onClick={handleCopy}
    >
      {copied ? 'Copied!' : (label || 'Copy UI Prompt')}
    </Button>
  );
};

/* ── AI-Ready Integration Prompts ────────────────────────────
   Developers copy these into Copilot / Cursor / ChatGPT to
   instantly scaffold UI for the API response data.
   ───────────────────────────────────────────────────────────── */

const UI_PROMPTS = {

/* ─── WhatsApp Template List ────────────────────────────────── */
'wa-template-list': `
## UI Integration Prompt — WhatsApp Template List

Build a React component that fetches and displays WhatsApp message templates
from the MessageOS External API as a responsive card grid.

### API Details
- **Endpoint:** POST /api/v1/external/templates/whatsapp/list
- **Auth Header:** x-api-key-id + x-api-secret headers  (or Authorization: Basic base64(key_id:secret))
- **Request Body:**
\`\`\`json
{ "page": 1, "limit": 20, "search": "" }
\`\`\`
- **Response Shape:**
\`\`\`json
{
  "success": true,
  "data": {
    "templates": [
      {
        "templateid": 10,
        "metatemplateid": "123456789",
        "templatename": "order_confirmation",
        "templatecategory": "utility",
        "templatestatus": "APPROVED",
        "templatelanguage": "en",
        "component": [
          { "type": "HEADER", "format": "IMAGE", "example": { "header_handle": ["https://..."] } },
          { "type": "BODY", "text": "Hello {{1}}, your order #{{2}} is confirmed." },
          { "type": "FOOTER", "text": "Reply STOP to unsubscribe" },
          { "type": "BUTTONS", "buttons": [
              { "type": "URL", "text": "Track Order", "url": "https://..." },
              { "type": "QUICK_REPLY", "text": "Contact Support" }
          ]}
        ],
        "created_at": "2025-01-10T08:00:00Z"
      }
    ],
    "pagination": { "page": 1, "limit": 20, "total": 5 }
  }
}
\`\`\`

### JSX Structure (WhatsApp chat-bubble style card)
\`\`\`jsx
function WhatsAppTemplateGrid({ templates, pagination, onPageChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
      {templates.map((tpl) => (
        <WhatsAppTemplateCard key={tpl.templateid} template={tpl} />
      ))}
    </div>
  );
}

function WhatsAppTemplateCard({ template }) {
  // Extract component parts
  const header = template.component?.find(c => c.type === 'HEADER');
  const body   = template.component?.find(c => c.type === 'BODY');
  const footer = template.component?.find(c => c.type === 'FOOTER');
  const btns   = template.component?.find(c => c.type === 'BUTTONS');

  const statusColors = {
    APPROVED: { bg: '#dcfce7', color: '#16a34a', label: 'Approved' },
    PENDING:  { bg: '#fef9c3', color: '#ca8a04', label: 'Pending' },
    REJECTED: { bg: '#fee2e2', color: '#dc2626', label: 'Rejected' },
  };
  const st = statusColors[template.templatestatus] || statusColors.PENDING;

  return (
    <div style={{
      background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden', display: 'flex', flexDirection: 'column'
    }}>
      {/* Header bar — green WhatsApp gradient */}
      <div style={{
        background: 'linear-gradient(135deg, #25d366, #128c7e)', padding: '12px 16px',
        color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{template.templatename}</div>
          <div style={{ fontSize: 11, opacity: 0.85 }}>
            {template.templatecategory?.toUpperCase()} • {template.templatelanguage?.toUpperCase()}
          </div>
        </div>
        <span style={{
          background: st.bg, color: st.color, padding: '2px 10px',
          borderRadius: 12, fontSize: 11, fontWeight: 600
        }}>{st.label}</span>
      </div>

      {/* Chat bubble body */}
      <div style={{ padding: 16, flex: 1, background: '#e5ddd5' }}>
        <div style={{
          background: '#fff', borderRadius: '0 8px 8px 8px', padding: 12,
          boxShadow: '0 1px 1px rgba(0,0,0,0.05)'
        }}>
          {/* Header media */}
          {header?.format === 'IMAGE' && (
            <img
              src={header.example?.header_handle?.[0] || ''}
              alt="header"
              style={{ width: '100%', borderRadius: 6, marginBottom: 8 }}
            />
          )}
          {header?.format === 'VIDEO' && <div style={{
            background: '#000', borderRadius: 6, height: 120, marginBottom: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
          }}>▶ Video</div>}
          {header?.text && <div style={{ fontWeight: 600, marginBottom: 4 }}>{header.text}</div>}

          {/* Body */}
          <div style={{
            fontSize: 14, color: '#303030', lineHeight: 1.5,
            whiteSpace: 'pre-wrap', marginBottom: footer ? 8 : 0
          }}>
            {body?.text?.replace(/\\{\\{(\\d+)\\}\\}/g, (_, n) => \`{{var_\${n}}}\`) || ''}
          </div>

          {/* Footer */}
          {footer?.text && (
            <div style={{ fontSize: 12, color: '#8696a0', marginTop: 4 }}>{footer.text}</div>
          )}

          {/* Timestamp */}
          <div style={{ textAlign: 'right', fontSize: 11, color: '#8696a0', marginTop: 4 }}>
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        {/* Buttons */}
        {btns?.buttons?.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {btns.buttons.map((btn, i) => (
              <button key={i} style={{
                width: '100%', padding: '8px 0', border: 'none', borderRadius: 8,
                background: '#fff', color: '#00a5f4', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', boxShadow: '0 1px 1px rgba(0,0,0,0.05)'
              }}>
                {btn.type === 'URL' ? '🔗 ' : btn.type === 'PHONE_NUMBER' ? '📞 ' : '↩️ '}
                {btn.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
\`\`\`

### Requirements
- Parse the \`component\` array to extract HEADER, BODY, FOOTER, BUTTONS parts.
- Show status badges: APPROVED (green), PENDING (amber), REJECTED (red).
- Show category + language in header bar (e.g. "UTILITY • EN").
- Render buttons below the chat bubble (URL, PHONE_NUMBER, QUICK_REPLY).
- Replace \`{{1}}\`, \`{{2}}\` variable placeholders with readable labels.
- Grid: 1 column on mobile, 2 on tablet, 3-4 on desktop.
- Add pagination controls using the \`pagination\` object from the response.
- Add a search input that passes the \`search\` param to the API.
`.trim(),

/* ─── WhatsApp Campaign Dashboard ───────────────────────────── */
'wa-campaign-dashboard': `
## UI Integration Prompt — WhatsApp Campaign Dashboard

Build a React component that displays WhatsApp campaign list with real-time
delivery stats from the MessageOS External API.

### API Details
**List Campaigns:** POST /api/v1/external/campaigns/whatsapp/list
\`\`\`json
// Request
{ "page": 1, "limit": 20, "search": "" }
// Response
{
  "success": true,
  "data": {
    "campaigns": [{
      "campaignid": 25, "campaignname": "January Promo", "templateid": 10,
      "templatename": "order_confirmation", "status": "draft",
      "totalcontacts": 150, "created_at": "2025-01-15T10:00:00Z"
    }],
    "pagination": { "page": 1, "limit": 20, "total": 3 }
  }
}
\`\`\`

**Campaign Stats:** POST /api/v1/external/campaigns/whatsapp/stats
\`\`\`json
// Request
{ "campaignid": 25 }
// Response
{
  "success": true,
  "data": { "total": 150, "sent": 148, "delivered": 140, "read": 95, "failed": 2, "pending": 0 }
}
\`\`\`

**Campaign Analytics:** GET /api/v1/external/campaigns/whatsapp/analytics/templates
\`\`\`json
{
  "success": true,
  "data": [{ "templateid": 10, "templatename": "order_confirmation", "total_sent": 500, "delivered": 480, "read": 320, "failed": 20 }]
}
\`\`\`

### JSX Structure
\`\`\`jsx
function CampaignDashboard({ campaigns }) {
  return (
    <div>
      {/* Stats Summary Bar */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: campaigns.length, color: '#1890ff' },
          { label: 'Draft', value: campaigns.filter(c => c.status === 'draft').length, color: '#8c8c8c' },
          { label: 'Completed', value: campaigns.filter(c => c.status === 'completed').length, color: '#52c41a' },
          { label: 'Processing', value: campaigns.filter(c => c.status === 'processing').length, color: '#faad14' },
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', borderRadius: 10, padding: '16px 24px', flex: '1 1 150px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: \`4px solid \${s.color}\`
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 13, color: '#8c8c8c' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Campaign Cards */}
      {campaigns.map(c => (
        <div key={c.campaignid} style={{
          background: '#fff', borderRadius: 10, padding: 20, marginBottom: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{c.campaignname}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              Template: {c.templatename} • {c.totalcontacts} contacts •
              {new Date(c.created_at).toLocaleDateString()}
            </div>
          </div>
          <CampaignStatusBadge status={c.status} />
        </div>
      ))}
    </div>
  );
}

function CampaignStatusBadge({ status }) {
  const map = {
    draft: { bg: '#f5f5f5', color: '#8c8c8c' },
    processing: { bg: '#fff7e6', color: '#fa8c16' },
    completed: { bg: '#f6ffed', color: '#52c41a' },
    failed: { bg: '#fff1f0', color: '#ff4d4f' },
    paused: { bg: '#e6f7ff', color: '#1890ff' },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{
      background: s.bg, color: s.color, padding: '4px 14px', borderRadius: 20,
      fontSize: 12, fontWeight: 600, textTransform: 'capitalize'
    }}>{status}</span>
  );
}

/* Stats Detail — render after fetching /campaigns/whatsapp/stats */
function CampaignStats({ stats }) {
  const bars = [
    { key: 'sent', label: 'Sent', color: '#1890ff' },
    { key: 'delivered', label: 'Delivered', color: '#52c41a' },
    { key: 'read', label: 'Read', color: '#722ed1' },
    { key: 'failed', label: 'Failed', color: '#ff4d4f' },
    { key: 'pending', label: 'Pending', color: '#faad14' },
  ];
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {bars.map(b => (
        <div key={b.key} style={{
          flex: '1 1 100px', background: '#fff', borderRadius: 8, padding: 14,
          textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: b.color }}>{stats[b.key] || 0}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{b.label}</div>
          <div style={{ fontSize: 11, color: '#bfbfbf' }}>
            {stats.total ? ((stats[b.key] / stats.total) * 100).toFixed(1) : 0}%
          </div>
        </div>
      ))}
    </div>
  );
}
\`\`\`

### Requirements
- Fetch campaign list and display as cards with status badges.
- On clicking a campaign, fetch its stats and show delivery breakdown.
- Status colors: draft (grey), processing (orange), completed (green), failed (red), paused (blue).
- Show percentage calculations for each stat (sent/total, delivered/total, etc.).
- Add search bar and pagination.
- Auth: x-api-key-id + x-api-secret headers (or Basic auth).
`.trim(),

/* ─── Email Template List ───────────────────────────────────── */
'email-template-list': `
## UI Integration Prompt — Email Template List & Preview

Build a React component that fetches and displays email templates from the
MessageOS External API as a searchable table (desktop) and card list (mobile).

### API Details
**List:** GET /api/v1/external/templates/email/?page=1&limit=20&category=
\`\`\`json
{
  "success": true,
  "data": {
    "templates": [{
      "templateId": 5,
      "name": "Welcome Email",
      "templateCode": "welcome_email_v1",
      "subject": "Welcome to {{company_name}}!",
      "category": "onboarding",
      "created_at": "2025-01-15T10:00:00Z"
    }],
    "pagination": { "page": 1, "limit": 20, "total": 5 }
  }
}
\`\`\`

**Get Detail:** GET /api/v1/external/templates/email/:templateId
\`\`\`json
{
  "success": true,
  "data": {
    "templateId": 5,
    "name": "Welcome Email",
    "templateCode": "welcome_email_v1",
    "subject": "Welcome to {{company_name}}!",
    "bodyContent": "<h1>Hello {{name}}</h1><p>Welcome aboard.</p>",
    "category": "onboarding",
    "header": {
      "companyName": "My Company",
      "tagline": "We make things simple",
      "logoUrl": "https://...",
      "bannerUrl": "https://..."
    },
    "body": {
      "greeting": "Hello {{name}}",
      "content": "Welcome to our platform...",
      "showButton": true,
      "buttonText": "Get Started",
      "buttonUrl": "https://..."
    },
    "footer": {
      "companyName": "My Company",
      "address": "123 Main St",
      "unsubscribeText": "Unsubscribe"
    },
    "socialLinks": {
      "facebook": "https://facebook.com/...",
      "twitter": "https://twitter.com/...",
      "linkedin": "https://linkedin.com/..."
    },
    "created_at": "2025-01-15T10:00:00Z",
    "updated_at": "2025-01-15T10:00:00Z"
  }
}
\`\`\`

**Preview:** GET /api/v1/external/templates/email/:templateId/preview
\`\`\`json
{ "success": true, "data": { "html": "<html>...rendered email HTML...</html>" } }
\`\`\`

**Categories:** GET /api/v1/external/templates/email/categories
\`\`\`json
{ "success": true, "data": ["onboarding", "marketing", "transactional", "newsletter"] }
\`\`\`

### JSX Structure
\`\`\`jsx
function EmailTemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ page: 1, limit: 20, category: '' });
  const [previewHtml, setPreviewHtml] = useState(null);

  // Fetch templates and categories on mount / filter change...

  const categoryColors = {
    onboarding: { bg: '#e6f7ff', color: '#1890ff' },
    marketing: { bg: '#fff7e6', color: '#fa8c16' },
    transactional: { bg: '#f6ffed', color: '#52c41a' },
    newsletter: { bg: '#f9f0ff', color: '#722ed1' },
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Search templates..." style={{
          flex: 1, minWidth: 200, padding: '8px 14px', borderRadius: 8,
          border: '1px solid #d9d9d9', fontSize: 14
        }} />
        <select style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d9d9d9' }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Desktop Table */}
      <table style={{
        width: '100%', borderCollapse: 'separate', borderSpacing: 0,
        background: '#fff', borderRadius: 10, overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
      }}>
        <thead>
          <tr style={{ background: '#fafafa' }}>
            <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Template</th>
            <th>Category</th>
            <th>Subject</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map(tpl => (
            <tr key={tpl.templateId} style={{ borderTop: '1px solid #f0f0f0' }}>
              <td style={{ padding: '12px 16px' }}>
                <div style={{ fontWeight: 600 }}>{tpl.name}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{tpl.templateCode}</div>
              </td>
              <td>
                <span style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                  background: categoryColors[tpl.category]?.bg || '#f5f5f5',
                  color: categoryColors[tpl.category]?.color || '#8c8c8c'
                }}>{tpl.category}</span>
              </td>
              <td style={{ fontSize: 13, color: '#595959', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tpl.subject}
              </td>
              <td style={{ fontSize: 12, color: '#8c8c8c' }}>
                {new Date(tpl.created_at).toLocaleDateString()}
              </td>
              <td>
                <button onClick={() => fetchPreview(tpl.templateId)}
                  style={{ background: 'none', border: '1px solid #d9d9d9', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>
                  Preview
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Preview Modal */}
      {previewHtml && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '90%', maxWidth: 700,
            maxHeight: '85vh', overflow: 'auto', padding: 0
          }}>
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid #f0f0f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ fontWeight: 600 }}>Email Preview</span>
              <button onClick={() => setPreviewHtml(null)} style={{
                background: 'none', border: 'none', fontSize: 18, cursor: 'pointer'
              }}>✕</button>
            </div>
            <iframe
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
              style={{ width: '100%', height: '70vh', border: 'none' }}
              title="Email Preview"
            />
          </div>
        </div>
      )}
    </div>
  );
}
\`\`\`

### Requirements
- Desktop: HTML table with sortable columns. Mobile: stack as cards.
- Category badges with color mapping (onboarding=blue, marketing=orange, etc.).
- Preview button fetches /templates/email/:id/preview and renders HTML in an iframe.
- Search input and category filter dropdown.
- Pagination controls from the response pagination object.
- Auth: x-api-key-id + x-api-secret headers (or Basic auth).
- Sanitize preview HTML: strip <script>, inline event handlers, and javascript: URLs.
`.trim(),

/* ─── Email Campaign Dashboard ──────────────────────────────── */
'email-campaign-dashboard': `
## UI Integration Prompt — Email Campaign Dashboard

Build a React component that displays email campaign management with analytics
from the MessageOS External API.

### API Details
**List:** GET /api/v1/external/campaigns/email/?page=1&limit=20
\`\`\`json
{
  "success": true,
  "data": {
    "campaigns": [{
      "campaignId": 12, "name": "January Newsletter", "templateId": 5,
      "status": "draft", "totalRecipients": 150, "created_at": "2025-01-15T10:00:00Z"
    }],
    "pagination": { "page": 1, "limit": 20, "total": 3 }
  }
}
\`\`\`

**Analytics:** GET /api/v1/external/campaigns/email/:campaignId/analytics
\`\`\`json
{
  "success": true,
  "data": {
    "campaignId": 12, "sent": 145, "delivered": 140, "opened": 85,
    "clicked": 30, "bounced": 5, "unsubscribed": 2,
    "openRate": 60.71, "clickRate": 21.43
  }
}
\`\`\`

**Recipients:** GET /api/v1/external/campaigns/email/:campaignId/recipients?page=1&limit=20
\`\`\`json
{
  "success": true,
  "data": {
    "recipients": [{
      "userid": 45, "username": "John Doe", "emailid": "john@example.com",
      "status": "delivered", "opened": true, "clicked": false,
      "sent_at": "2025-01-15T12:00:00Z"
    }],
    "pagination": { "page": 1, "limit": 20, "total": 145 }
  }
}
\`\`\`

**SES Status:** GET /api/v1/external/campaigns/email/ses-status
\`\`\`json
{
  "success": true,
  "data": {
    "verified": true, "sendingEnabled": true, "dailyQuota": 50000,
    "sentLast24Hours": 1200, "verifiedDomains": ["example.com"],
    "verifiedEmails": ["noreply@example.com"]
  }
}
\`\`\`

### JSX Structure
\`\`\`jsx
function EmailCampaignDashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // Fetch campaigns on mount...

  return (
    <div>
      {/* Campaign List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {campaigns.map(c => (
          <div key={c.campaignId} onClick={() => fetchAnalytics(c.campaignId)}
            style={{
              background: '#fff', borderRadius: 10, padding: 20,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              cursor: 'pointer', border: selectedCampaign === c.campaignId ? '2px solid #1890ff' : '2px solid transparent',
              transition: 'border 0.2s'
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {c.totalRecipients} recipients • {new Date(c.created_at).toLocaleDateString()}
                </div>
              </div>
              <EmailCampaignStatusBadge status={c.status} />
            </div>
          </div>
        ))}
      </div>

      {/* Analytics Panel — shown when a campaign is selected */}
      {analytics && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Campaign Analytics</h3>

          {/* Metric Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Sent', value: analytics.sent, color: '#1890ff' },
              { label: 'Delivered', value: analytics.delivered, color: '#52c41a' },
              { label: 'Opened', value: analytics.opened, color: '#722ed1' },
              { label: 'Clicked', value: analytics.clicked, color: '#13c2c2' },
              { label: 'Bounced', value: analytics.bounced, color: '#ff4d4f' },
              { label: 'Unsubscribed', value: analytics.unsubscribed, color: '#fa8c16' },
            ].map(m => (
              <div key={m.label} style={{
                background: '#fff', borderRadius: 10, padding: 16, textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
              }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: m.color }}>{m.value}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{m.label}</div>
              </div>
            ))}
          </div>

          {/* Open & Click Rate Bars */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {[
              { label: 'Open Rate', value: analytics.openRate, color: '#722ed1' },
              { label: 'Click Rate', value: analytics.clickRate, color: '#13c2c2' },
            ].map(r => (
              <div key={r.label} style={{ flex: '1 1 200px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{r.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: r.color }}>{r.value}%</span>
                </div>
                <div style={{ background: '#f0f0f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                  <div style={{
                    width: r.value + '%', height: '100%', borderRadius: 4,
                    background: r.color, transition: 'width 0.6s ease'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmailCampaignStatusBadge({ status }) {
  const map = {
    draft: { bg: '#f5f5f5', color: '#8c8c8c' },
    sending: { bg: '#e6f7ff', color: '#1890ff' },
    completed: { bg: '#f6ffed', color: '#52c41a' },
    paused: { bg: '#fff7e6', color: '#fa8c16' },
    failed: { bg: '#fff1f0', color: '#ff4d4f' },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{
      background: s.bg, color: s.color, padding: '4px 14px', borderRadius: 20,
      fontSize: 12, fontWeight: 600, textTransform: 'capitalize'
    }}>{status}</span>
  );
}
\`\`\`

### Requirements
- Campaign list with clickable cards, show analytics on click.
- Analytics: metric cards + open/click rate progress bars.
- Status badges matching: draft (grey), sending (blue), completed (green), paused (orange), failed (red).
- Recipients table: show per-user delivery status, opened/clicked booleans.
- Add SES status indicator (green dot if verified & sending enabled).
- Auth: x-api-key-id + x-api-secret headers (or Basic auth).
- Pagination for both campaign list and recipients.
`.trim(),

/* ─── Contacts & Addressbook UI ─────────────────────────────── */
'contacts-addressbook': `
## UI Integration Prompt — Contacts & Addressbook Management UI

Build a React component for managing contacts and addressbooks using
the MessageOS External API.

### API Details
**List Addressbooks:** GET /api/v1/external/addressbook/list
\`\`\`json
{
  "success": true,
  "data": [
    { "bookid": 1, "addressbookname": "Customers", "totalusers": 150, "created_at": "2025-01-01T00:00:00Z" },
    { "bookid": 2, "addressbookname": "Leads", "totalusers": 80, "created_at": "2025-01-05T00:00:00Z" }
  ]
}
\`\`\`

**List Contacts:** POST /api/v1/external/contacts/list
\`\`\`json
// Request
{ "bookid": 1, "page": 1, "limit": 20, "search": "" }
// Response
{
  "success": true,
  "data": {
    "users": [{
      "userid": 45, "username": "John Doe", "mobileno": "9876543210",
      "countrycode": "91", "emailid": "john@example.com"
    }],
    "pagination": { "page": 1, "limit": 20, "total": 150 }
  }
}
\`\`\`

**Contact Details:** POST /api/v1/external/contacts/details
\`\`\`json
// Request
{ "userid": 45 }
// Response
{
  "success": true,
  "data": {
    "userid": 45, "username": "John Doe", "mobileno": "9876543210",
    "countrycode": "91", "emailid": "john@example.com", "address": "123 Main St",
    "bookid": 1, "userdata": { "customField1": "value1" },
    "created_at": "2025-01-10T08:00:00Z"
  }
}
\`\`\`

**Add Contact:** POST /api/v1/external/contacts/add
\`\`\`json
// Request
{ "username": "Jane Doe", "mobileno": "9876543211", "countrycode": "91", "bookid": 1, "emailid": "jane@example.com" }
// Response
{ "success": true, "data": {}, "meta": { "requestId": "...", "timestamp": "..." } }
\`\`\`

### JSX Structure
\`\`\`jsx
function ContactManager() {
  const [books, setBooks] = useState([]);
  const [activeBook, setActiveBook] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0 }}>
      {/* Left: Addressbook sidebar */}
      <aside style={{
        width: 260, background: '#fafafa', borderRight: '1px solid #f0f0f0',
        padding: 16, overflowY: 'auto'
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Addressbooks</h3>
        {books.map(b => (
          <div key={b.bookid} onClick={() => setActiveBook(b.bookid)}
            style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 4, cursor: 'pointer',
              background: activeBook === b.bookid ? '#e6f7ff' : 'transparent',
              border: activeBook === b.bookid ? '1px solid #91d5ff' : '1px solid transparent'
            }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{b.addressbookname}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{b.totalusers} contacts</div>
          </div>
        ))}
      </aside>

      {/* Right: Contacts table */}
      <main style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search contacts..."
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d9d9d9', width: 280 }} />
          <button style={{
            background: '#1890ff', color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 20px', fontWeight: 600, cursor: 'pointer'
          }}>+ Add Contact</button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>Name</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map(c => (
              <tr key={c.userid} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={{ padding: '10px 14px', fontWeight: 500 }}>{c.username}</td>
                <td style={{ fontSize: 13 }}>+{c.countrycode} {c.mobileno}</td>
                <td style={{ fontSize: 13, color: '#595959' }}>{c.emailid || '—'}</td>
                <td>
                  <button style={{ background: 'none', border: '1px solid #d9d9d9', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Edit</button>
                  <button style={{ background: 'none', border: '1px solid #ffa39e', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12, color: '#ff4d4f' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </div>
  );
}
\`\`\`

### Requirements
- Left sidebar for addressbook selection, right panel for contacts.
- Search, add, edit, delete contacts with proper API calls.
- Display phone with country code formatted (e.g. +91 9876543210).
- Pagination and bulk delete support.
- Auth: x-api-key-id + x-api-secret headers (or Basic auth).
`.trim(),

};  // end UI_PROMPTS

const EndpointCard = ({ method, path, scope, description, bodyTable, queryTable, body, requestExample, responseExample, errorExample, notes }) => (
  <Card className="endpoint-card" size="small" style={{ marginBottom: 16 }}>
    <div style={{ marginBottom: 8 }}>
      <MethodBadge method={method} />
      <span className="endpoint-path">{path}</span>
      {scope && <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>{scope}</Tag>}
    </div>
    {description && <div className="endpoint-desc">{description}</div>}

    {requestExample && (
      <div style={{ marginTop: 8 }}>
        <Text strong style={{ fontSize: 13 }}>Request Body:</Text>
        <CodeBlock>{typeof requestExample === 'string' ? requestExample : JSON.stringify(requestExample, null, 2)}</CodeBlock>
      </div>
    )}

    {body && !requestExample && (
      <div style={{ marginTop: 8 }}>
        <Text strong style={{ fontSize: 13 }}>Request Body:</Text>
        <CodeBlock>{typeof body === 'string' ? body : JSON.stringify(body, null, 2)}</CodeBlock>
      </div>
    )}

    {queryTable && queryTable.length > 0 && (
      <div style={{ marginTop: 12 }}>
        <Text strong style={{ fontSize: 13 }}>Query Parameters:</Text>
        <Table
          dataSource={queryTable}
          columns={[
            { title: 'Param', dataIndex: 'field', key: 'field', render: (v) => <Text code>{v}</Text> },
            { title: 'Type', dataIndex: 'type', key: 'type' },
            { title: 'Default', dataIndex: 'default', key: 'default' },
            { title: 'Description', dataIndex: 'description', key: 'description' },
          ]}
          pagination={false}
          size="small"
          rowKey="field"
          style={{ marginTop: 8 }}
        />
      </div>
    )}

    {responseExample && (
      <div style={{ marginTop: 8 }}>
        <Text strong style={{ fontSize: 13 }}>Response:</Text>
        <CodeBlock>{typeof responseExample === 'string' ? responseExample : JSON.stringify(responseExample, null, 2)}</CodeBlock>
      </div>
    )}

    {errorExample && (
      <div style={{ marginTop: 8 }}>
        <Text strong style={{ fontSize: 13, color: '#ff4d4f' }}>Error Example:</Text>
        <CodeBlock>{typeof errorExample === 'string' ? errorExample : JSON.stringify(errorExample, null, 2)}</CodeBlock>
      </div>
    )}

    {notes && (
      <Alert type="warning" showIcon message={notes} style={{ marginTop: 8, fontSize: 12 }} banner />
    )}
  </Card>
);

/* ═════════════════════════════════════════════════════════════
   TAB 1 — Getting Started
   ═════════════════════════════════════════════════════════════ */

const GettingStartedTab = () => (
  <div>
    <Title level={4}>Getting Started</Title>

    <Paragraph><Text strong>External API Base URL:</Text></Paragraph>
    <div className="auth-block"><code>https://api.messageos.io/api/v1/external</code></div>

    <Alert
      type="info"
      showIcon
      message="Rate Limit"
      description="60 requests / minute per API key. Headers X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset are returned on every response."
      style={{ marginBottom: 16 }}
    />

    <Title level={5} style={{ marginTop: 20 }}>Authentication</Title>
    <Paragraph>
      All External API requests must include your API key in the <Text code>x-api-key</Text> header:
    </Paragraph>
    <div className="auth-block">
      <code>x-api-key: sk_live_xxxxxxxxxxxxxxxxxxxx</code><br />
      <code>Content-Type: application/json</code>
    </div>
    <Paragraph type="secondary" style={{ marginTop: 8 }}>
      Every API key has <strong>scopes</strong> that control which endpoints it can call. Calling an endpoint without the required scope returns <Text code>403 FORBIDDEN_SCOPE</Text>.
    </Paragraph>

    <Divider />

    <Title level={5}>Idempotency</Title>
    <Paragraph>
      Mutative send endpoints (<Text code>POST /messages/whatsapp/send</Text> and <Text code>/send/bulk</Text>) require the <Text code>Idempotency-Key</Text> header to prevent duplicate sends:
    </Paragraph>
    <div className="auth-block">
      <code>Idempotency-Key: your-unique-key-uuid-here</code>
    </div>
    <Paragraph type="secondary" style={{ marginTop: 8 }}>
      Use a UUID per request. If you retry with the same key, the server returns the original response without re-sending.
    </Paragraph>

    <Divider />

    <Title level={5}>Response Envelope</Title>
    <Paragraph>All responses follow a consistent shape:</Paragraph>
    <CodeBlock>{`// Success
{
  "success": true,
  "data": { ... },
  "meta": {
    "requestId": "uuid",
    "timestamp": "ISO-8601"
  }
}

// Error
{
  "success": false,
  "error": {
    "code": "ERROR_CODE",
    "message": "Human-readable message"
  },
  "meta": {
    "requestId": "uuid",
    "timestamp": "ISO-8601"
  }
}`}</CodeBlock>

    <Divider />

    <Divider />

    <Title level={5}><SafetyOutlined /> Health Check — <Text code>GET /health</Text></Title>
    <Paragraph type="secondary">No scope required. Use to verify connectivity and see active scopes.</Paragraph>
    <CodeBlock>{`curl https://api.messageos.io/api/v1/external/health \\
  -H "x-api-key: sk_live_xxxx"

// Response 200
{
  "success": true,
  "data": {
    "status": "ok",
    "company": "Acme Corp",
    "scopes": ["messages:send", "webhooks:manage"],
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}</CodeBlock>

    <Divider />

    <Title level={5}>Quick Start — Step 1: Test connectivity</Title>
    <CodeBlock>{`const res = await fetch('https://api.messageos.io/api/v1/external/health', {
  headers: { 'x-api-key': 'sk_live_xxxx' }
});
const { data } = await res.json();
console.log('Company:', data.company, 'Scopes:', data.scopes);`}</CodeBlock>

    <Title level={5} style={{ marginTop: 24 }}>Quick Start — Step 2: Send a message</Title>
    <CodeBlock>{`const res = await fetch('https://api.messageos.io/api/v1/external/messages/whatsapp/send', {
  method: 'POST',
  headers: {
    'x-api-key': 'sk_live_xxxx',
    'Content-Type': 'application/json',
    'Idempotency-Key': crypto.randomUUID(),
  },
  body: JSON.stringify({
    to: '+919876543210',
    template: { name: 'order_confirmation' },
    variables: { '1': 'Rahul', '2': 'ORD-123', '3': '₹999' },
    client_ref: 'order_123',
  }),
});
const { data } = await res.json();
console.log('Message ID:', data.message_id); // tx_...`}</CodeBlock>

    <Title level={5} style={{ marginTop: 24 }}>Quick Start — Step 3: Set up a BYO Webhook</Title>
    <CodeBlock>{`const res = await fetch('https://api.messageos.io/api/v1/external/webhooks/subscriptions', {
  method: 'POST',
  headers: {
    'x-api-key': 'sk_live_xxxx',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    url: 'https://your-server.com/webhook',
    events: ['message.delivered', 'message.failed', 'message.read'],
    description: 'Production webhook',
  }),
});
const { data } = await res.json();
// ⚠️  SAVE data.secret — it is shown only ONCE
console.log('Secret:', data.secret);
console.log('Subscription ID:', data.subscription_id);`}</CodeBlock>

    <Title level={5} style={{ marginTop: 24 }}>Quick Start — Step 4: Poll message status</Title>
    <CodeBlock>{`const res = await fetch(\`https://api.messageos.io/api/v1/external/messages/whatsapp/\${messageId}\`, {
  headers: { 'x-api-key': 'sk_live_xxxx' }
});
const { data } = await res.json();
// data.status: 'queued' | 'sent' | 'delivered' | 'read' | 'failed'
// data.events: [{ status, at }, ...]`}</CodeBlock>

    <Title level={5} style={{ marginTop: 24 }}>Quick Example — cURL</Title>
    <CodeBlock>{`curl -X POST \\
  https://api.messageos.io/api/v1/external/contacts/add \\
  -H "x-api-key: sk_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "username": "Rahul Sharma",
    "mobileno": "9876543210",
    "countrycode": "91",
    "bookid": "addressbook-uuid"
  }'`}</CodeBlock>

    <Title level={5} style={{ marginTop: 24 }}>Quick Example — Python</Title>
    <CodeBlock>{`import requests

r = requests.post(
    'https://api.messageos.io/api/v1/external/contacts/add',
    headers={'x-api-key': 'sk_live_xxxx'},
    json={
        'username': 'Rahul Sharma',
        'mobileno': '9876543210',
        'countrycode': '91',
        'bookid': 'addressbook-uuid',
    }
)
print(r.json())`}</CodeBlock>

  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB 2 — Dashboard API Key Management (7 endpoints)
   ═════════════════════════════════════════════════════════════ */

const ApiKeyManagementTab = () => (
  <div>
    <Title level={4}>API Key Management (Dashboard)</Title>
    <Paragraph type="secondary">
      Base: <Text code>/api/api-keys</Text> — Uses <strong>JWT cookie</strong> from dashboard login (not x-api-key-id/x-api-secret).
      <br />
      Response format: <Text code>{`{ status, statuscode, message, entity }`}</Text>
    </Paragraph>

    {/* 4.1 Generate API Key */}
    <EndpointCard
      method="POST"
      path="/api/api-keys/generate"
      description="Generate a new API key. The raw key is returned ONLY on creation — store it securely!"
      bodyTable={[
        { field: 'keyName', type: 'string', required: false, description: 'Label for the key (default: "Default")' },
        { field: 'scopes', type: 'string[]', required: true, description: 'Array of scope strings (see Scopes Reference)' },
        { field: 'rateLimitPerMinute', type: 'number', required: false, description: 'Requests per minute (default: 60)' },
        { field: 'expiresAt', type: 'ISO-8601', required: false, description: 'Expiration date (null = never)' },
        { field: 'webhookUrl', type: 'string', required: false, description: 'URL for webhook delivery' },
        { field: 'webhookEvents', type: 'string[]', required: false, description: 'Events to trigger webhooks' },
      ]}
      requestExample={{
        keyName: 'My Integration',
        scopes: ['contacts:read', 'contacts:write', 'campaigns:read'],
        rateLimitPerMinute: 60,
        expiresAt: '2025-12-31T23:59:59Z',
        webhookUrl: 'https://yourserver.com/webhook',
        webhookEvents: ['campaign.sent', 'contact.created'],
      }}
      responseExample={{
        status: true,
        statuscode: 201,
        message: 'API key created successfully. Store the api_key securely — it cannot be retrieved again.',
        entity: {
          id: 1,
          company_id: 42,
          key_name: 'My Integration',
          key_id: 'mk_id_a1b2c3d4e5f6g7h8',
          api_secret: 'mk_secret_9f8e7d6c5b4a3d2e...',   // ⚠️ shown once
          key_id_display: 'mk_id_a1b2...g7h8',
          scopes: ['contacts:read', 'contacts:write', 'campaigns:read'],
          rate_limit_per_minute: 60,
          expires_at: '2025-12-31T23:59:59Z',
          webhook_url: 'https://yourserver.com/webhook',
          webhook_events: ['campaign.sent', 'contact.created'],
          status: 'active',
          created_at: '2025-01-15T10:00:00Z',
        },
      }}
      notes="⚠️ api_key is returned ONLY on creation. Store it securely — it cannot be retrieved again!"
    />

    {/* 4.2 List API Keys */}
    <EndpointCard
      method="GET"
      path="/api/api-keys/list"
      description="List all API keys for your company (raw key is never returned)"
      responseExample={{
        status: true,
        statuscode: 200,
        message: 'API keys retrieved',
        entity: [
          {
            id: 1,
            key_name: 'My Integration',
            key_id: 'mk_id_a1b2c3d4e5f6g7h8',
            scopes: ['contacts:read', 'contacts:write'],
            rate_limit_per_minute: 60,
            status: 'active',
            expires_at: '2025-12-31T23:59:59Z',
            webhook_url: 'https://yourserver.com/webhook',
            webhook_events: ['campaign.sent'],
            last_used_at: '2025-01-15T12:00:00Z',
            created_at: '2025-01-15T10:00:00Z',
          },
        ],
      }}
    />

    {/* 4.3 Get Available Scopes */}
    <EndpointCard
      method="GET"
      path="/api/api-keys/scopes"
      description="Get all available scopes and their descriptions"
      responseExample={{
        status: true,
        statuscode: 200,
        message: 'Available scopes',
        entity: {
          scopes: [
            'contacts:read', 'contacts:write', 'templates:read', 'templates:write',
            'email_templates:read', 'email_templates:write',
            'campaigns:read', 'campaigns:write', 'campaigns:execute',
            'email_campaigns:read', 'email_campaigns:write', 'email_campaigns:execute',
          ],
          description: {
            'contacts:read': 'Read contacts and addressbook data',
            'contacts:write': 'Create, update, delete contacts and addressbooks',
            'templates:read': 'Read WhatsApp templates',
            'templates:write': 'Create, update, delete WhatsApp templates',
            'campaigns:read': 'Read WhatsApp campaign data and analytics',
            'campaigns:write': 'Create and update WhatsApp campaigns',
            'campaigns:execute': 'Send/start WhatsApp campaigns',
          },
        },
      }}
    />

    {/* 4.4 Update API Key */}
    <EndpointCard
      method="PUT"
      path="/api/api-keys/:id"
      description="Update an API key (all fields are optional — send only what you want to change)"
      bodyTable={[
        { field: 'keyName', type: 'string', required: false, description: 'Updated key label' },
        { field: 'scopes', type: 'string[]', required: false, description: 'Updated scopes array' },
        { field: 'rateLimitPerMinute', type: 'number', required: false, description: 'Updated rate limit' },
        { field: 'webhookUrl', type: 'string', required: false, description: 'Updated webhook URL' },
        { field: 'webhookEvents', type: 'string[]', required: false, description: 'Updated webhook events' },
      ]}
      requestExample={{
        keyName: 'Updated Name',
        scopes: ['contacts:read', 'templates:read'],
        rateLimitPerMinute: 120,
        webhookUrl: 'https://newurl.com/webhook',
        webhookEvents: ['contact.created'],
      }}
      responseExample={{
        status: true,
        statuscode: 200,
        message: 'API key updated',
        entity: {
          id: 1,
          key_name: 'Updated Name',
          scopes: ['contacts:read', 'templates:read'],
          rate_limit_per_minute: 120,
          webhook_url: 'https://newurl.com/webhook',
          webhook_events: ['contact.created'],
          status: 'active',
        },
      }}
    />

    {/* 4.5 Revoke API Key */}
    <EndpointCard
      method="PUT"
      path="/api/api-keys/:id/revoke"
      description="Revoke an API key (permanently deactivates it)"
      bodyTable={[
        { field: 'reason', type: 'string', required: false, description: 'Reason for revocation' },
      ]}
      requestExample={{ reason: 'Key compromised' }}
      responseExample={{
        status: true,
        statuscode: 200,
        message: 'API key revoked successfully',
        entity: { id: 1, status: 'revoked', revoked_at: '2025-01-15T14:00:00Z' },
      }}
    />

    {/* 4.6 Rotate API Key */}
    <EndpointCard
      method="PUT"
      path="/api/api-keys/:id/rotate"
      description="Rotate an API key (old key is immediately revoked, new key returned once)"
      responseExample={{
        status: true,
        statuscode: 201,
        message: 'API key rotated. Store the new api_key securely.',
        entity: {
          id: 2,
          api_secret: 'mk_secret_new9f8e7d6c5b...',   // ⚠️ shown once
          key_id: 'mk_id_a1b2c3d4e5f6g7h8',
          key_name: 'My Integration',
          scopes: ['contacts:read', 'contacts:write'],
          rate_limit_per_minute: 60,
          status: 'active',
          created_at: '2025-01-15T14:05:00Z',
        },
      }}
      notes="⚠️ Old key is immediately revoked. New api_key shown only once — store it securely!"
    />

    {/* 4.7 Get Audit Logs */}
    <EndpointCard
      method="GET"
      path="/api/api-keys/audit-logs?page=1&limit=50"
      description="Get paginated audit logs for all API key usage"
      queryTable={[
        { field: 'page', type: 'number', default: '1', description: 'Page number' },
        { field: 'limit', type: 'number', default: '50', description: 'Items per page (max 100)' },
      ]}
      responseExample={{
        status: true,
        statuscode: 200,
        message: 'Audit logs retrieved',
        entity: {
          logs: [
            {
              id: 1,
              api_key_id: 1,
              method: 'POST',
              path: '/api/v1/external/contacts/add',
              status_code: 200,
              ip_address: '1.2.3.4',
              created_at: '2025-01-15T12:00:00Z',
            },
          ],
          pagination: { page: 1, limit: 50, total: 120 },
        },
      }}
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB 3 — Contacts API (9 endpoints)
   ═════════════════════════════════════════════════════════════ */

const ContactsTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>Contacts API</Title>
      <CopyPromptButton promptKey="contacts-addressbook" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary">
      Base: <Text code>/api/v1/external/contacts</Text> — Requires <Tag color="blue">contacts:read</Tag> and/or <Tag color="blue">contacts:write</Tag> scopes.
    </Paragraph>

    {/* 8.1 Add Single Contact */}
    <EndpointCard
      method="POST"
      path="/contacts/add"
      scope="contacts:write"
      description="Add a single contact to an address book"
      requestExample={{
        mobileno: '9876543210',
        username: 'Rahul Sharma',
        countrycode: '91',
        bookid: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890',
        emailid: 'rahul@example.com',
        address: 'Mumbai, India',
        userdata: { order_id: 'ORD-001', tier: 'gold' },
      }}
      responseExample={{
        success: true,
        data: { userid: 'user-uuid', mobileno: '9876543210', username: 'Rahul Sharma' },
      }}
      notes="If the contact was previously soft-deleted, it will be restored. bookid is a UUID."
    />

    {/* 8.2 Bulk Add Contacts */}
    <EndpointCard
      method="POST"
      path="/contacts/add-bulk"
      scope="contacts:write"
      description="Add up to 500 contacts in one call"
      requestExample={{
        bookid: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890',
        contacts: [
          { mobileno: '9876543210', username: 'Rahul Sharma', countrycode: '91', emailid: 'rahul@example.com', userdata: { tier: 'gold' } },
          { mobileno: '8765432109', username: 'Priya Patel', countrycode: '91' },
        ],
      }}
      responseExample={{
        success: true,
        data: {
          total: 2,
          inserted: 2,
          failed: 0,
          results: {
            success: [
              { mobileno: '9876543210', id: 'user-uuid-1', restored: false },
              { mobileno: '8765432109', id: 'user-uuid-2' },
            ],
            failed: [],
          },
        },
      }}
    />

    {/* 8.3 Get Contact Details */}
    <EndpointCard
      method="POST"
      path="/contacts/details"
      scope="contacts:read"
      description="Get full details of a single contact"
      requestExample={{ mobileno: '9876543210', bookid: 'addressbook-uuid' }}
      responseExample={{
        success: true,
        data: { userid: 'user-uuid', username: 'Rahul Sharma', mobileno: '9876543210', countrycode: '91', emailid: 'rahul@example.com', userdata: { tier: 'gold' } },
      }}
    />

    {/* 8.4 List Contacts */}
    <EndpointCard
      method="POST"
      path="/contacts/list"
      scope="contacts:read"
      description="Paginate contacts within a specific addressbook"
      requestExample={{
        bookid: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890',
        page: 1,
        limit: 50,
      }}
      responseExample={{
        success: true,
        data: {
          users: [
            { userid: 'a1b2c3d4-0000-0000-0000-000000000001', username: 'Rahul Sharma', mobileno: '9876543210', countrycode: '91' },
          ],
          pagination: { page: 1, limit: 50, total: 1 },
        },
      }}
    />

    {/* 8.5 Update Contact */}
    <EndpointCard
      method="POST"
      path="/contacts/update"
      scope="contacts:write"
      description="Update contact details"
      requestExample={{
        mobileno: '9876543210',
        username: 'Rahul Kumar',
        emailid: 'rahul.kumar@example.com',
        userdata: { tier: 'platinum' },
      }}
      responseExample={{
        success: true,
        message: 'Updated successfully.',
        data: {},
      }}
    />

    {/* 8.6 Delete Contact */}
    <EndpointCard
      method="POST"
      path="/contacts/delete"
      scope="contacts:write"
      description="Soft-delete a single contact"
      requestExample={{ mobileno: '9876543210', bookid: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890' }}
      responseExample={{ success: true, message: 'Deleted successfully.', data: {} }}
    />

    {/* 8.7 Bulk Delete Contacts */}
    <EndpointCard
      method="POST"
      path="/contacts/delete-bulk"
      scope="contacts:write"
      description="Delete multiple contacts"
      requestExample={{ bookid: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890', mobilenos: ['9876543210', '8765432109'] }}
      responseExample={{ success: true, message: 'Bulk delete complete', data: { deletedCount: 2 } }}
    />

    {/* 8.8 Get Labels */}
    <EndpointCard
      method="POST"
      path="/contacts/labels"
      scope="contacts:read"
      description="Get all labels used in an addressbook"
      requestExample={{ bookid: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890' }}
      responseExample={{ success: true, data: { labels: ['VIP', 'New', 'Premium'] } }}
    />

    {/* 8.9 Label Users */}
    <EndpointCard
      method="POST"
      path="/contacts/label-users"
      scope="contacts:write"
      description="Apply a label to multiple contacts"
      requestExample={{
        bookid: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890',
        userids: ['user-uuid-1', 'user-uuid-2'],
        label: 'VIP',
      }}
      responseExample={{ success: true, message: 'Labels applied', data: { updated: 2 } }}
    />
  </div>
);

/* ═══ TAB 3 — Addressbooks API (6 endpoints) */

const AddressbookTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>Addressbook API</Title>
      <CopyPromptButton promptKey="contacts-addressbook" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary">
      Base: <Text code>/api/v1/external/addressbook</Text> — Requires <Tag color="blue">contacts:read</Tag> and/or <Tag color="blue">contacts:write</Tag> scopes.
    </Paragraph>

    {/* 9.1 List All Addressbooks */}
    <EndpointCard
      method="GET"
      path="/addressbook/list"
      scope="contacts:read"
      description="List all addressbooks for the company"
      responseExample={{
        success: true,
        data: [
          { bookid: '550e...', bookname: 'Premium Customers', contactcount: 1250, created_at: '2025-01-01T00:00:00.000Z' },
        ],
      }}
    />

    {/* 9.2 Create Addressbook */}
    <EndpointCard
      method="POST"
      path="/addressbook/create"
      scope="contacts:write"
      description="Create a new addressbook with optional custom columns"
      requestExample={{
        bookname: 'New Customer List',
        columns: [
          { name: 'order_value', type: 'number' },
          { name: 'tier', type: 'text' },
        ],
      }}
      responseExample={{
        success: true,
        message: 'Addressbook created',
        data: { bookid: 'new-uuid', bookname: 'New Customer List' },
      }}
    />

    {/* 9.3 Get Addressbook Schema */}
    <EndpointCard
      method="GET"
      path="/addressbook/schema?bookid=addressbook-uuid"
      scope="contacts:read"
      description="Get the column schema of an addressbook"
      responseExample={{
        success: true,
        data: {
          columns: [
            { column_name: 'userid', data_type: 'uuid' },
            { column_name: 'username', data_type: 'character varying' },
            { column_name: 'order_value', data_type: 'numeric' },
          ],
        },
      }}
    />

    {/* 9.4 Get Users in Addressbook */}
    <EndpointCard
      method="POST"
      path="/addressbook/users"
      scope="contacts:read"
      description="Paginate users within a specific addressbook"
      requestExample={{ bookid: 'addressbook-uuid', page: 1, limit: 50 }}
      responseExample={{
        success: true,
        data: [
          { userid: 'uuid-1', username: 'Rahul Sharma', mobileno: '9876543210' },
        ],
        meta: { count: 1 },
      }}
    />

    {/* 9.5 Update Addressbook User */}
    <EndpointCard
      method="POST"
      path="/addressbook/user/update"
      scope="contacts:write"
      description="Update a contact's fields in the addressbook"
      requestExample={{ userid: 'user-uuid', username: 'Updated Name', tier: 'platinum', labelnames: ['vip'] }}
      responseExample={{ success: true, message: 'User updated', data: { userid: 'user-uuid' } }}
      notes="Only send fields you want to update."
    />

    {/* 9.6 Delete Addressbook User */}
    <EndpointCard
      method="POST"
      path="/addressbook/user/delete"
      scope="contacts:write"
      description="Delete one or more users from an addressbook"
      requestExample={{ userid: ['uuid-1', 'uuid-2'] }}
      responseExample={{ success: true, message: 'Users deleted', data: [{ userid: 'uuid-1' }, { userid: 'uuid-2' }] }}
    />

    {/* 9.7 Delete Addressbook */}
    <EndpointCard
      method="POST"
      path="/addressbook/delete"
      scope="contacts:write"
      description="Delete an entire addressbook and all its contacts"
      requestExample={{ bookid: 'book-uuid' }}
      responseExample={{ success: true, message: 'Addressbook deleted', data: {} }}
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB 4 — WhatsApp Templates API (7 endpoints + 3-step upload)
   ═════════════════════════════════════════════════════════════ */

const WhatsAppTemplatesTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>WhatsApp Templates API</Title>
      <CopyPromptButton promptKey="wa-template-list" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary" style={{ marginTop: 8 }}>
      Base: <Text code>/api/v1/external/templates/whatsapp</Text> — Requires <Tag color="blue">templates:read</Tag> and/or <Tag color="blue">templates:write</Tag> scopes.
    </Paragraph>
    <Alert
      type="warning"
      showIcon
      icon={<WarningOutlined />}
      message="WABA Required"
      description="All WhatsApp template endpoints require your company to have completed Meta Embedded Signup (WABA setup). Returns WABA_NOT_CONFIGURED error otherwise."
      style={{ marginBottom: 16 }}
    />

    {/* 10.1 Create Template */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/create"
      scope="templates:write"
      description="Create a text template or a media-header template (using pre-uploaded file handle)"
      requestExample={{
        templatename: 'order_confirmation',
        templatecategory: 'utility',
        templatelanguage: 'en_US',
        component: [
          { type: 'HEADER', format: 'TEXT', text: 'Order Confirmed ✓' },
          { type: 'BODY', text: 'Hi {{1}}, your order {{2}} has been confirmed. Total: {{3}}', example: { body_text: [['Rahul', 'ORD-001', '₹999']] } },
          { type: 'FOOTER', text: 'Reply STOP to unsubscribe' },
        ],
      }}
      responseExample={{
        success: true,
        message: 'Template submitted to Meta',
        data: { templateid: 'uuid-123', metatemplateid: '123456789012345', templatename: 'order_confirmation', templatestatus: 'PENDING', templatelanguage: 'en_US' },
      }}
    />

    {/* 10.2 List Templates */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/list"
      scope="templates:read"
      description="List and filter templates"
      requestExample={{ page: 1, limit: 20, status: 'APPROVED', category: 'utility' }}
      responseExample={{
        success: true,
        data: {
          templates: [{ templateid: 'uuid-123', templatename: 'order_confirmation', templatecategory: 'utility', templatestatus: 'APPROVED', templatelanguage: 'en_US', created_at: '2025-01-10T08:00:00Z' }],
          pagination: { page: 1, limit: 20, total: 5 },
        },
      }}
    />

    {/* 10.3 Update Template */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/update"
      scope="templates:write"
      description="Update an existing template"
      requestExample={{ templateid: 'uuid', templatename: 'updated_name', templatelanguage: 'en_US', component: ['...'], metatemplateid: '123456789012345' }}
      responseExample={{ success: true, message: 'Template updated', data: { templateid: 'uuid', templatestatus: 'PENDING' } }}
    />

    {/* 10.4 Delete Template */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/delete"
      scope="templates:write"
      description="Delete template(s) by id or name"
      requestExample={{ templateid: 'template-uuid' }}
      responseExample={{ success: true, message: 'Template deleted', data: {} }}
      notes="Either templateid or templatename is required."
    />

    {/* 10.5 Get Template Counts */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/counts"
      scope="templates:read"
      description="Get template count breakdown by category and approval status"
      requestExample={{}}
      responseExample={{
        success: true,
        data: [
          { category: 'marketing', status: 'APPROVED', count: 8 },
          { category: 'utility', status: 'PENDING', count: 3 },
        ],
      }}
    />

    {/* 10.6 Upload Media for Template */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/upload-media"
      scope="templates:write"
      description="Upload a file (multipart/form-data) to get a Meta media handle for template creation"
      body={`Content-Type: multipart/form-data\n\nFields:\n  file (binary) — Required\n\nFile limits: image/jpeg image/png (5 MB), video/mp4 (16 MB), application/pdf (100 MB)`}
      responseExample={{
        success: true,
        data: { fileHandle: '4:uploaded_handle_xyz', mimeType: 'image/png' },
      }}
    />

    <Divider />
    <Title level={5}><CloudUploadOutlined /> Three-Step Media Upload Flow</Title>
    <Paragraph type="secondary">Use this 3-step flow to upload media and create a template with a media header.</Paragraph>

    {/* 7.6 Step 1 — Start Upload Session */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/start-upload-session"
      scope="templates:write"
      description="Step 1: Start an upload session"
      requestExample={{
        fileName: 'banner.png',
        fileType: 'image/png',
        fileSize: 123456,
      }}
      responseExample={{
        success: true,
        data: {
          uploadSessionId: 'upload_session_abc123',
          expiresAt: '...',
        },
      }}
      notes="File types: image/jpeg, image/png, image/gif, image/webp, application/pdf, video/mp4, video/3gpp. Max 5 MB images, 100 MB others."
    />

    {/* 7.6 Step 2 — Upload File Data */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/upload-file-data"
      scope="templates:write"
      description="Step 2: Upload the file binary"
      body={`Content-Type: multipart/form-data\n\nFields:\n  file (binary) — Required\n  uploadSessionId (string) — Required (from Step 1)\n  type (string) — Optional MIME type (default image/jpeg)`}
      responseExample={{
        success: true,
        data: {
          fileHandle: '4:uploaded_handle_xyz',
          mimeType: 'image/png',
          s3Key: 'template-media/...',
        },
      }}
    />

    {/* 7.6 Step 3 — Create Template with File Handle */}
    <EndpointCard
      method="POST"
      path="/templates/whatsapp/create-with-file-handle"
      scope="templates:write"
      description="Step 3: Create template with the uploaded file handle"
      requestExample={{
        templatename: 'promo_img_v1',
        templatecategory: 'marketing',
        templatelanguage: 'en_US',
        templatecontent: 'Check out our new product!',
        component: [
          { type: 'HEADER', format: 'IMAGE' },
          { type: 'BODY', text: 'Check out our new product!' },
        ],
        fileHandle: '4:uploaded_handle_xyz',
        headerFormat: 'IMAGE',
        s3Key: 'template-media/companyid/...',
      }}
      responseExample={{
        success: true,
        message: 'Template with media created',
        data: {
          templateid: 'uuid',
          templatestatus: 'PENDING',
        },
      }}
      notes="headerFormat must be IMAGE, DOCUMENT, or VIDEO."
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB 5 — WhatsApp Campaigns API (13 endpoints)
   ═════════════════════════════════════════════════════════════ */

const WhatsAppCampaignsTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>WhatsApp Campaigns API</Title>
      <CopyPromptButton promptKey="wa-campaign-dashboard" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary" style={{ marginTop: 8 }}>
      Base: <Text code>/api/v1/external/campaigns/whatsapp</Text> — Requires <Tag color="blue">campaigns:read</Tag>, <Tag color="blue">campaigns:write</Tag>, and/or <Tag color="blue">campaigns:execute</Tag> scopes.
    </Paragraph>
    <Alert
      type="warning"
      showIcon
      icon={<WarningOutlined />}
      message="WABA Required"
      description="All WhatsApp campaign endpoints require WABA setup (Meta Embedded Signup)."
      style={{ marginBottom: 16 }}
    />

    {/* 11.1 Create Campaign — Basic */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/create"
      scope="campaigns:write"
      description="Create a new campaign (text-only template)"
      requestExample={{
        campaignname: 'January Sale',
        templateid: 'template-uuid',
        bookid: 'addressbook-uuid',
        selectionType: 'all',
        templatemapping: {
          variabletype: 'number',
          mapping: { '{{1}}': 'username', '{{2}}': 'userdata.order_id' },
        },
        ratedetails: {
          category: 'marketing',
          contactsdetails: [{ whatsapppricingid: 'pricing-uuid', countrycode: '+91', count: 100, category: 'marketing', pricepermessage: 0.02, totalprice: 2.00, meta_cost: 0.0118 }],
          totalContacts: 100,
          finalPrice: 2.00,
        },
      }}
      responseExample={{
        success: true,
        message: 'Campaign created',
        data: { campaignid: 'campaign-uuid', campaignname: 'January Sale', status: 'draft', totalcontacts: 100 },
      }}
      notes="selectionType: 'all' | 'filter' | 'selection'. templatemapping.mapping uses dot-notation for userdata sub-fields: 'userdata.order_id'."
    />

    {/* 11.1b Create Campaign — Dynamic Per-Contact Media */}
    <Alert
      type="info"
      showIcon
      message="Dynamic Per-Contact Media (headerMediaColumn)"
      description={(
        <div>
          <p>When the template has a <Text code>DOCUMENT</Text>, <Text code>IMAGE</Text>, or <Text code>VIDEO</Text> header and each contact should receive a different file, add <Text code>headerMediaColumn</Text> and <Text code>headerMediaType</Text> to <Text code>templatemapping</Text>:</p>
          <CodeBlock>{JSON.stringify({
            templatemapping: {
              variabletype: 'number',
              mapping: { '{{1}}': 'username' },
              headerMediaColumn: 'userdata.pdf_link',
              headerMediaType: 'document',
            },
          }, null, 2)}</CodeBlock>
          <p style={{ marginTop: 8 }}><strong>headerMediaColumn</strong>: addressbook column holding the per-contact https:// URL. Use dot-notation for userdata sub-fields (<Text code>userdata.pdf_link</Text>) or a top-level column name (<Text code>pdf_link</Text>).</p>
          <p><strong>headerMediaType</strong>: <Text code>document</Text> / <Text code>image</Text> / <Text code>video</Text> — must match the template HEADER format.</p>
          <p>URL rules: each contact's URL must start with <Text code>https://</Text>. Missing or non-https URLs are skipped gracefully — the body still sends.</p>
        </div>
      )}
      style={{ marginBottom: 16 }}
    />

    {/* 11.2 List Campaigns */}}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/list"
      scope="campaigns:read"
      description="List campaigns with pagination"
      requestExample={{
        campaignname: 'Diwali',
        page: 1,
        pageSize: 10,
      }}
      responseExample={{
        success: true,
        data: {
          campaigns: [{ campaignid: 'uuid', campaignname: 'Diwali Promo', status: 'draft', totalcontacts: 150, created_at: '2025-01-10T08:00:00Z' }],
          pagination: { page: 1, pageSize: 10, total: 5 },
        },
      }}
    />

    {/* 11.3 Get Campaign Details */}}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/details"
      scope="campaigns:read"
      description="Get full campaign details"
      requestExample={{
        campaignid: 'campaign-uuid',
      }}
      responseExample={{
        success: true,
        data: {
          campaignid: 'uuid',
          campaignname: 'Diwali Promo',
          templateid: 'uuid',
          status: 'sent',
          totalcontacts: 150,
          sent: 148,
          delivered: 140,
          read: 100,
          failed: 2,
          created_at: '2025-01-10T08:00:00Z',
        },
      }}
    />

    {/* 11.4 Get Campaign Transactions */}}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/transactions"
      scope="campaigns:read"
      description="Get per-message delivery status"
      requestExample={{
        campaignid: 'campaign-uuid',
        status: 2,
        page: 1,
        pageSize: 10,
      }}
      responseExample={{
        success: true,
        data: {
          transactions: [{ userid: 'uuid', mobileno: '9876543210', status: 2, statusLabel: 'delivered', sent_at: '2025-01-10T08:05:00Z' }],
          pagination: { page: 1, pageSize: 10, total: 140 },
        },
      }}
      notes="Status codes: 0=pending, 1=sent, 2=delivered, 3=read, 4=failed, 5=opt-out, 6=retry."
    />

    {/* 11.5 Send Campaign */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/send"
      scope="campaigns:execute"
      description="Queue a campaign for sending"
      requestExample={{ campaignid: 'campaign-uuid' }}
      responseExample={{ success: true, message: 'Campaign queued for sending', data: { campaignid: 'uuid', status: 'sending' } }}
    />

    {/* 8.6 Get Campaign Stats */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/stats"
      scope="campaigns:read"
      description="Get aggregate statistics for a campaign"
      requestExample={{
        campaignid: 'campaign-uuid',
      }}
      responseExample={{
        success: true,
        data: { total: 150, sent: 148, delivered: 140, read: 100, failed: 2, pending: 0, optout: 8 },
      }}
    />

    {/* 8.7 Campaign Progress */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/progress"
      scope="campaigns:read"
      description="Live progress snapshot"
      requestExample={{
        campaignid: 'campaign-uuid',
      }}
      responseExample={{
        success: true,
        data: { campaignid: 'uuid', status: 'sending', total: 150, processed: 120, remaining: 30, percentComplete: 80 },
      }}
    />

    {/* 8.8 Add Users to Campaign */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/add-users"
      scope="campaigns:write"
      description="Add users to an existing campaign"
      requestExample={{
        campaignid: 'campaign-uuid',
        userids: ['uuid-1', 'uuid-2'],
      }}
      responseExample={{
        success: true,
        message: 'Users added',
        data: { added: 2, total: 152 },
      }}
    />

    {/* 8.9 Remove Users from Campaign */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/remove-users"
      scope="campaigns:write"
      description="Remove users from a campaign"
      requestExample={{
        campaignid: 'campaign-uuid',
        userids: ['uuid-1'],
      }}
      responseExample={{
        success: true,
        message: 'Users removed',
        data: { removed: 1, total: 151 },
      }}
    />

    {/* 8.10 Preview Selection */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/preview-selection"
      scope="campaigns:read"
      description="Preview how many users match a selection type"
      requestExample={{
        selectionType: 'filter',
        category: 'customer',
      }}
      responseExample={{
        success: true,
        data: { matchedCount: 85 },
      }}
    />

    {/* 8.11 Get User Categories */}
    <EndpointCard
      method="GET"
      path="/campaigns/whatsapp/categories"
      scope="campaigns:read"
      description="List unique user categories"
      responseExample={{
        success: true,
        data: { categories: ['customer', 'lead', 'vip'] },
      }}
    />

    {/* 8.12 Template Analytics */}
    <EndpointCard
      method="GET"
      path="/campaigns/whatsapp/analytics/templates"
      scope="campaigns:read"
      description="Template-level analytics"
      responseExample={{
        success: true,
        data: [{ templateid: 'uuid', templatename: 'welcome', totalSent: 500, delivered: 480, read: 300, failed: 20 }],
      }}
    />

    {/* 8.13 Analytics Overview */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/analytics/overview"
      scope="campaigns:read"
      description="Multi-period analytics overview"
      requestExample={{
        templateid: 'uuid',
      }}
      responseExample={{
        success: true,
        data: {
          today: { sent: 50, delivered: 48 },
          week: { sent: 200, delivered: 190 },
          month: { sent: 800, delivered: 750 },
        },
      }}
      notes="templateid is optional — if omitted, aggregates across all templates."
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB 6 — Email Templates API (11 endpoints)
   ═════════════════════════════════════════════════════════════ */

const EmailTemplatesTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>Email Templates API</Title>
      <CopyPromptButton promptKey="email-template-list" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary">
      Base: <Text code>/api/v1/external/templates/email</Text> — Requires <Tag color="blue">email_templates:read</Tag> and/or <Tag color="blue">email_templates:write</Tag> scopes.
    </Paragraph>

    {/* 9.1 Create Email Template */}
    <EndpointCard
      method="POST"
      path="/templates/email"
      scope="email_templates:write"
      description="Create an email template"
      requestExample={{
        name: 'Welcome v1',
        subject: 'Welcome to {{company}}!',
        bodyContent: '<h1>Hello {{name}}</h1><p>Thanks for joining.</p>',
        category: 'transactional',
        channel: 'email',
        headerLogoUrl: 'https://example.com/logo.png',
        headerTitle: 'Welcome',
        bodyGreeting: 'Hi {{name}}',
        ctaButtonText: 'Get Started',
        ctaButtonUrl: 'https://example.com/start',
        footerSignature: 'The Team',
        footerShowUnsubscribe: true,
        variables: { name: { type: 'name' }, company: { type: 'text' } },
      }}
      responseExample={{
        success: true,
        message: 'Template created',
        data: {
          id: 1,
          template_uuid: 'uuid-123',
          name: 'Welcome v1',
          subject: 'Welcome to {{company}}!',
          category: 'transactional',
          created_at: '2025-01-10T08:00:00Z',
        },
      }}
      notes="Also accepts multipart/form-data with file fields: logoImage, bannerImage, footerImage (5 MB each) + 'data' JSON field."
    />

    {/* 9.2 List Email Templates */}
    <EndpointCard
      method="GET"
      path="/templates/email?page=1&limit=20&category=transactional"
      scope="email_templates:read"
      description="List templates with pagination"
      queryTable={[
        { param: 'page', type: 'int', default: '1', description: 'Page number' },
        { param: 'limit', type: 'int', default: '20', description: 'Items per page' },
        { param: 'search', type: 'string', default: '—', description: 'Search by name' },
        { param: 'category', type: 'string', default: '—', description: 'Filter by category' },
      ]}
      responseExample={{
        success: true,
        data: {
          templates: [{ id: 1, template_uuid: 'uuid-123', name: 'Welcome v1', category: 'transactional', subject: 'Welcome!' }],
          pagination: { page: 1, limit: 20, total: 5 },
        },
      }}
    />

    {/* 9.3 List Categories */}
    <EndpointCard
      method="GET"
      path="/templates/email/categories"
      scope="email_templates:read"
      description="List available template categories"
      responseExample={{ success: true, data: ['transactional', 'marketing', 'notification', 'onboarding'] }}
    />

    {/* 9.4 Preview Template (from data) */}
    <EndpointCard
      method="POST"
      path="/templates/email/preview"
      scope="email_templates:read"
      description="Render template HTML without saving"
      requestExample={{
        subject: 'Hello {{name}}',
        bodyContent: '<p>Hello {{name}}</p>',
        variables: { name: 'John' },
      }}
      responseExample={{
        success: true,
        data: { html: '<html>..rendered..</html>', subject: 'Hello John' },
      }}
      notes="Either bodyContent or bodyHtml is required."
    />

    {/* 9.5 Validate HTML */}
    <EndpointCard
      method="POST"
      path="/templates/email/validate-html"
      scope="email_templates:read"
      description="Validate HTML content"
      requestExample={{
        html: '<p>Hello {{name}}</p>',
      }}
      responseExample={{
        success: true,
        data: { valid: true, warnings: [] },
      }}
    />

    {/* 9.6 Upload Asset */}
    <EndpointCard
      method="POST"
      path="/templates/email/upload"
      scope="email_templates:write"
      description="Upload an image or attachment (multipart/form-data, max 10 MB)"
      body={`Content-Type: multipart/form-data

Fields:
  file (binary) — Required
  type (string) — Optional: logo | banner | footer | body | attachment | general

Accepts: images, PDF, DOC/DOCX, XLS/XLSX, TXT`}
      responseExample={{
        success: true,
        data: { url: '/api/email/media/...', s3Key: 'email-templates/...', fileName: 'banner.png', fileSize: 123456 },
      }}
    />

    {/* 9.7 Get Template by ID */}
    <EndpointCard
      method="GET"
      path="/templates/email/:id"
      scope="email_templates:read"
      description="Get a specific template"
      responseExample={{
        success: true,
        data: { id: 1, template_uuid: 'uuid-123', name: 'Welcome v1', subject: 'Welcome!', bodyContent: '<h1>Hello</h1>', category: 'transactional' },
      }}
    />

    {/* 9.8 Preview Saved Template */}
    <EndpointCard
      method="GET"
      path="/templates/email/:id/preview"
      scope="email_templates:read"
      description="Render a saved template"
      responseExample={{
        success: true,
        data: { html: '<html>..rendered..</html>', subject: 'Welcome John' },
      }}
    />

    {/* 9.9 Update Template */}
    <EndpointCard
      method="PUT"
      path="/templates/email/:id"
      scope="email_templates:write"
      description="Update a template"
      requestExample={{
        subject: 'Updated Subject',
        bodyContent: '<p>Updated content</p>',
      }}
      responseExample={{
        success: true,
        message: 'Template updated',
        data: { id: 1, name: 'Welcome v1' },
      }}
      notes="Only supply keys you want to change. Proxy URLs for images are silently ignored — upload fresh assets."
    />

    {/* 9.10 Delete Template */}
    <EndpointCard
      method="DELETE"
      path="/templates/email/:id"
      scope="email_templates:write"
      description="Delete a template"
      responseExample={{
        success: true,
        message: 'Template deleted successfully',
      }}
    />

    {/* 9.11 Duplicate Template */}
    <EndpointCard
      method="POST"
      path="/templates/email/:id/duplicate"
      scope="email_templates:write"
      description="Duplicate a template"
      requestExample={{
        name: 'Welcome v2',
      }}
      responseExample={{
        success: true,
        message: 'Template duplicated',
        data: { id: 2, template_uuid: 'new-uuid', name: 'Welcome v2' },
      }}
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB 7 — Email Campaigns API (15 endpoints)
   ═════════════════════════════════════════════════════════════ */

const EmailCampaignsTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>Email Campaigns API</Title>
      <CopyPromptButton promptKey="email-campaign-dashboard" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary">
      Base: <Text code>/api/v1/external/campaigns/email</Text> — Requires <Tag color="blue">email_campaigns:read</Tag>, <Tag color="blue">email_campaigns:write</Tag>, and/or <Tag color="blue">email_campaigns:execute</Tag> scopes.
    </Paragraph>

    {/* 10.1 Create Email Campaign */}
    <EndpointCard
      method="POST"
      path="/campaigns/email"
      scope="email_campaigns:write"
      description="Create an email campaign"
      requestExample={{
        name: 'April Newsletter',
        templateId: 'template-uuid',
        addressbookId: 'book-uuid',
        subject: 'Your April Update',
        fromName: 'Acme Corp',
        replyTo: 'reply@acme.com',
        selectionType: 'all',
        sendingMode: 'platform_domain',
      }}
      responseExample={{
        success: true,
        message: 'Campaign created',
        data: {
          id: 1,
          campaign_uuid: 'campaign-uuid',
          name: 'April Newsletter',
          status: 'draft',
          totalRecipients: 150,
        },
      }}
      notes="sendingMode: 'platform_domain' (default) | 'custom_from' | 'verified_domain'. scheduleTime (ISO string) → status becomes 'scheduled'."
    />

    {/* 10.2 List Email Campaigns */}
    <EndpointCard
      method="GET"
      path="/campaigns/email?page=1&limit=20"
      scope="email_campaigns:read"
      description="List campaigns with filters"
      queryTable={[
        { field: 'page', type: 'int', default: '1', description: 'Page number' },
        { field: 'limit', type: 'int', default: '20', description: 'Items per page' },
        { field: 'status', type: 'string', default: '—', description: 'Filter by status' },
        { field: 'search', type: 'string', default: '—', description: 'Search by name' },
      ]}
      responseExample={{
        success: true,
        data: {
          campaigns: [{
            id: 1,
            campaign_uuid: 'uuid',
            name: 'April Newsletter',
            status: 'draft',
            totalRecipients: 150,
            created_at: '2025-01-10T08:00:00Z',
          }],
          pagination: { page: 1, limit: 20, total: 5 },
        },
      }}
    />

    {/* 10.3 Get Sending Config */}
    <EndpointCard
      method="GET"
      path="/campaigns/email/sending-config"
      scope="email_campaigns:read"
      description="Get available sending modes and SES info"
      responseExample={{
        success: true,
        data: {
          availableModes: ['platform_domain', 'custom_from', 'verified_domain'],
          platformFromEmail: 'noreply@platform.com',
          verifiedDomains: ['mail.acme.com'],
        },
      }}
    />

    {/* 10.4 Get Email Campaign by ID */}
    <EndpointCard
      method="GET"
      path="/campaigns/email/:id"
      scope="email_campaigns:read"
      description="Get a campaign with its full template"
      responseExample={{
        success: true,
        data: {
          id: 1,
          campaign_uuid: 'uuid',
          name: 'April Newsletter',
          status: 'draft',
          subject: 'Your April Update',
          totalRecipients: 150,
          template: { id: 1, name: 'Welcome v1', subject: '...' },
        },
      }}
    />

    {/* 10.5 Update Email Campaign */}
    <EndpointCard
      method="PUT"
      path="/campaigns/email/:id"
      scope="email_campaigns:write"
      description="Update a campaign (only draft or scheduled)"
      requestExample={{
        subject: 'Updated Subject',
        fromName: 'Updated Name',
      }}
      responseExample={{
        success: true,
        message: 'Campaign updated',
        data: { id: 1, name: 'April Newsletter' },
      }}
    />

    {/* 10.6 Delete Email Campaign */}
    <EndpointCard
      method="DELETE"
      path="/campaigns/email/:id"
      scope="email_campaigns:write"
      description="Delete a campaign"
      responseExample={{
        success: true,
        message: 'Campaign deleted successfully',
      }}
    />

    {/* 10.7 Get Cost Estimate */}
    <EndpointCard
      method="GET"
      path="/campaigns/email/:id/cost"
      scope="email_campaigns:read"
      description="Estimate cost and check wallet balance"
      responseExample={{
        success: true,
        data: {
          totalRecipients: 150,
          costPerEmail: 0.001,
          estimatedCost: 0.15,
          walletBalance: 10.00,
          sufficient: true,
        },
      }}
    />

    {/* 10.8 Send Email Campaign */}
    <EndpointCard
      method="POST"
      path="/campaigns/email/:id/send"
      scope="email_campaigns:execute"
      description="Queue the campaign for sending"
      responseExample={{
        success: true,
        message: 'Campaign queued for sending',
        data: { id: 1, status: 'sending' },
      }}
    />

    {/* 10.9 Pause Email Campaign */}
    <EndpointCard
      method="POST"
      path="/campaigns/email/:id/pause"
      scope="email_campaigns:execute"
      description="Pause a sending campaign"
      responseExample={{
        success: true,
        message: 'Campaign paused',
      }}
    />

    {/* 10.10 Resume Email Campaign */}
    <EndpointCard
      method="POST"
      path="/campaigns/email/:id/resume"
      scope="email_campaigns:execute"
      description="Resume a paused campaign"
      responseExample={{
        success: true,
        message: 'Campaign resumed',
      }}
    />

    {/* 10.11 Duplicate Email Campaign */}
    <EndpointCard
      method="POST"
      path="/campaigns/email/:id/duplicate"
      scope="email_campaigns:write"
      description="Duplicate a campaign"
      requestExample={{
        name: 'April Newsletter (Copy)',
      }}
      responseExample={{
        success: true,
        message: 'Campaign duplicated',
        data: { id: 2, campaign_uuid: 'new-uuid', name: 'April Newsletter (Copy)' },
      }}
    />

    {/* 10.12 Send Test Email */}
    <EndpointCard
      method="POST"
      path="/campaigns/email/:id/test"
      scope="email_campaigns:write"
      description="Send a test email to a single address"
      requestExample={{
        testEmail: 'test@example.com',
      }}
      responseExample={{
        success: true,
        message: 'Test email sent successfully',
      }}
    />

    {/* 10.13 Validate Campaign Recipients */}
    <EndpointCard
      method="POST"
      path="/campaigns/email/:id/validate-recipients"
      scope="email_campaigns:read"
      description="Validate MX records for campaign recipients (samples up to 1000)"
      responseExample={{
        success: true,
        data: {
          totalChecked: 150,
          validCount: 145,
          invalidCount: 5,
          invalid: [{ email: 'bad@fake.xyz', reason: 'MX lookup failed' }],
        },
      }}
    />

    {/* 10.14 Get Email Campaign Analytics */}
    <EndpointCard
      method="GET"
      path="/campaigns/email/:id/analytics"
      scope="email_campaigns:read"
      description="Detailed analytics for a sent campaign"
      responseExample={{
        success: true,
        data: {
          totalSent: 150,
          delivered: 145,
          opened: 100,
          clicked: 50,
          bounced: 5,
          unsubscribed: 2,
          complained: 0,
          openRate: 68.9,
          clickRate: 34.4,
        },
      }}
    />

    {/* 10.15 Get Campaign Transactions */}
    <EndpointCard
      method="GET"
      path="/campaigns/email/:id/transactions?page=1&limit=50"
      scope="email_campaigns:read"
      description="Per-recipient transaction list"
      queryTable={[
        { field: 'page', type: 'int', default: '1', description: 'Page number' },
        { field: 'limit', type: 'int', default: '50', description: 'Items per page' },
        { field: 'status', type: 'string', default: '—', description: 'Filter status' },
        { field: 'search', type: 'string', default: '—', description: 'Search by email' },
      ]}
      responseExample={{
        success: true,
        data: {
          transactions: [{
            email: 'john@example.com',
            status: 'delivered',
            sentAt: '2025-01-10T08:05:00Z',
            openedAt: '2025-01-10T09:00:00Z',
          }],
          pagination: { page: 1, limit: 50, total: 150 },
        },
      }}
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB — WhatsApp Transactional API (Phase 3 / §9)
   ═════════════════════════════════════════════════════════════ */

const WhatsAppTransactionalTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>WhatsApp Transactional API</Title>
      <CopyPromptButton promptKey="wa-transactional" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary">
      Stateless one-off WhatsApp template sends — perfect for OTPs, order confirmations, shipment updates and other system-generated messages. The recipient is <Text strong>not</Text> stored in your address book. Every request is authenticated via <Text code>x-api-key</Text> header (not the dashboard JWT).
    </Paragraph>

    {/* ── Overview ── */}
    <Alert
      type="info"
      showIcon
      message="How it works"
      description={(
        <div>
          <p style={{ marginBottom: 6 }}>
            Your backend POSTs to <Text code>/api/v1/external/messages/whatsapp/send</Text> and receives <b>HTTP 202</b> immediately with a <Text code>message_id</Text>. MessageOS enqueues the message via BullMQ → submits to Meta → delivers to the user's phone.
          </p>
          <Text type="secondary" style={{ fontSize: 12 }}>
            202 Accepted = queued (not yet delivered). Use <Text code>message_id</Text> to poll status or receive it via webhook callback.
          </Text>
        </div>
      )}
      style={{ marginBottom: 16, borderRadius: 8 }}
    />

    {/* Auth */}
    <Alert
      type="info"
      showIcon
      message="Authentication — API Key header"
      description={(
        <div>
          Every request must include your API key in the request header:
          <CodeBlock>{`x-api-key: YOUR_API_KEY\nContent-Type: application/json`}</CodeBlock>
          Generate API keys from <b>Settings → API Keys</b> in the dashboard.
        </div>
      )}
      style={{ marginBottom: 16, borderRadius: 8 }}
    />

    <Alert
      type="info"
      showIcon
      message="When to use Transactional vs Campaign"
      description={(
        <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
          <li><b>Transactional</b> — single recipient, sent immediately, no campaign object created. Best for OTPs &amp; system events.</li>
          <li><b>Campaign</b> — list of recipients, optional scheduling, full delivery analytics. Best for marketing &amp; broadcasts.</li>
        </ul>
      )}
      style={{ marginBottom: 16, borderRadius: 8 }}
    />

    {/* ── 1. Send Single ── */}
    <Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>1 · Send a Single Message</Title>

    <EndpointCard
      method="POST"
      path="/messages/whatsapp/send"
      scope="messages:send"
      description="Send a single WhatsApp template message. Returns HTTP 202 immediately with a message_id and 'queued' status — delivery is asynchronous. Supports plain text body variables and dynamic media headers (document, image, video)."
      requestExample={`// Text-only template
{
  "to": "+919172768730",
  "template": {
    "name": "order_confirmed",
    "language": "en_US"
  },
  "variables": {
    "1": "Haris",
    "2": "ORD-98765",
    "3": "₹1,200"
  },
  "client_ref": "ORDER-99001",
  "callback_url": "https://yourapp.com/webhooks/wa-status"
}

// With a dynamic document/PDF header
{
  "to": "+919172768730",
  "template": {
    "name": "report_summary",
    "language": "en_US"
  },
  "variables": {
    "1": "Haris",
    "2": "MH30OR6665",
    "3": "Nagpur",
    "4": "90",
    "5": "54"
  },
  "header_media_url": "https://cdn.yourapp.com/reports/trip_report.pdf",
  "header_media_type": "document",
  "client_ref": "ORDER-99001",
  "callback_url": "https://yourapp.com/webhooks/wa-status"
}

// Alternative: using a Meta-uploaded media ID
{
  "to": "+919172768730",
  "template": { "name": "report_summary", "language": "en_US" },
  "variables": { "1": "Haris" },
  "media": {
    "id": "1234567890",
    "type": "document"
  }
}`}
      bodyTable={[
        { field: 'to', type: 'string', required: 'yes', description: 'Recipient phone in E.164 format — must include + and country code. e.g. +919172768730' },
        { field: 'template.name', type: 'string', required: 'yes', description: 'Exact name of the approved Meta template (lowercase with underscores, e.g. report_summary).' },
        { field: 'template.language', type: 'string', required: 'yes', description: 'Language code matching the template — e.g. en_US, hi, mr, ar.' },
        { field: 'variables', type: 'object', required: 'if template has body vars', description: 'Map of "1" → value for each body placeholder {{1}}, {{2}}, etc. Must provide all placeholders.' },
        { field: 'header_media_url', type: 'string', required: 'if template has document/image/video header', description: 'Publicly accessible https:// URL for the header media. Meta fetches this server-side — must be internet-reachable with no auth required. Min expiry: 1 hour.' },
        { field: 'header_media_type', type: 'string', required: 'no', description: 'Type of header media: document (default), image, or video.' },
        { field: 'media.id', type: 'string', required: 'no', description: 'Meta-uploaded media ID (alternative to header_media_url). No URL validation required. Expires after ~30 days.' },
        { field: 'client_ref', type: 'string', required: 'no', description: 'Your own order / reference ID — echoed back in all status callbacks and inbox queries.' },
        { field: 'callback_url', type: 'string', required: 'no', description: 'HTTPS URL where MessageOS POSTs delivery status updates. Must return HTTP 200 within 10 seconds.' },
        { field: 'category', type: 'string', required: 'no', description: 'Template category: utility, authentication, or marketing.' },
      ]}
      responseExample={{
        success: true,
        data: {
          message_id: 'tx_2cebb94fa5f0b0135b8c097d74760157',
          status: 'queued',
          client_ref: 'ORDER-99001',
          to_hash: '7735bce7d39e70a562bebe23ecb74f6b4ddee76b4b4ae73381d4c8a67adb20ac',
          to_country_code: '91',
          template: 'report_summary',
          template_id: '38398f0c-1ffb-4681-83f7-d1a18021cf86',
          accepted_at: '2026-04-27T10:21:23.863Z',
        },
        meta: { timestamp: '2026-04-27T10:21:23.900Z' },
      }}
      errorExample={{
        success: false,
        error: { code: 'INVALID_MEDIA_URL', message: '`header_media_url` must be a publicly accessible https:// URL. Local file paths and http:// links are not supported by Meta. Please upload your file to S3, a CDN, or your own public server and provide the https:// URL.' },
      }}
      notes="HTTP 202 Accepted means the message is queued — not yet delivered. Use GET /messages/whatsapp/:message_id to poll status. The to_hash in the response is the SHA-256 of the phone number — use it to query the inbox without exposing raw phone numbers."
    />

    {/* Document URL rules */}
    <Alert
      type="warning"
      showIcon
      message="header_media_url — URL requirements (⚠ #1 integration mistake)"
      description={(
        <div>
          <p style={{ marginBottom: 8 }}>Meta fetches the document <b>server-side</b> when delivering the message. The URL must be:</p>
          <Table
            size="small"
            pagination={false}
            style={{ marginBottom: 8 }}
            columns={[
              { title: 'Requirement', dataIndex: 'req', key: 'req', width: 130 },
              { title: '✅ Valid', dataIndex: 'valid', key: 'valid', width: 220, render: v => <Text type="success">{v}</Text> },
              { title: '❌ Invalid', dataIndex: 'invalid', key: 'invalid', render: v => <Text type="danger">{v}</Text> },
            ]}
            dataSource={[
              { key: 1, req: 'Protocol', valid: 'https://', invalid: 'http://, ftp://' },
              { key: 2, req: 'Accessibility', valid: 'Publicly reachable from internet', invalid: 'Local file path, localhost, 192.168.x.x' },
              { key: 3, req: 'Auth', valid: 'Unauthenticated GET', invalid: 'Requires login or token' },
              { key: 4, req: 'Lifetime', valid: 'Available ≥ 1 hour (recommend 24h)', invalid: 'Pre-signed URL with < 60s expiry' },
            ]}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Valid: S3 pre-signed URLs (expiry ≥ 3600s), public S3/GCS objects, CDN links, your own https:// server.
            Invalid paths return <Text code>INVALID_MEDIA_URL</Text> immediately.
          </Text>
          <Divider style={{ margin: '8px 0' }} />
          <Text strong style={{ fontSize: 12 }}>How to get a public https:// URL:</Text>
          <ul style={{ marginBottom: 0, paddingLeft: 18, fontSize: 12, marginTop: 4 }}>
            <li><b>AWS S3</b> — upload + generate a pre-signed URL with <Text code>expiresIn: 86400</Text> (24h)</li>
            <li><b>S3 public</b> — upload with <Text code>ACL: "public-read"</Text> for a permanent URL</li>
            <li><b>Google Cloud Storage</b> — upload with <Text code>public: true</Text></li>
            <li><b>Your own server</b> — serve from an <Text code>https://</Text> domain with <Text code>express.static</Text></li>
          </ul>
        </div>
      )}
      style={{ marginBottom: 16, borderRadius: 8 }}
    />

    {/* ── 2. Bulk Send ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>2 · Bulk Send (up to 100 messages)</Title>

    <EndpointCard
      method="POST"
      path="/messages/whatsapp/send/bulk"
      scope="messages:send"
      description="Send up to 100 messages in a single request. Each message is validated and processed independently — if one fails validation, others still go through. For 1000+ users, make multiple batches of ≤ 100."
      requestExample={{
        messages: [
          {
            to: '+919172768730',
            template: { name: 'report_summary', language: 'en_US' },
            variables: { '1': 'Haris', '2': 'MH30OR6665', '3': 'Nagpur' },
            header_media_url: 'https://cdn.example.com/reports/haris_trip.pdf',
            client_ref: 'ORDER-001',
          },
          {
            to: '+919876543210',
            template: { name: 'report_summary', language: 'en_US' },
            variables: { '1': 'Arjun', '2': 'MH12AB1234', '3': 'Pune' },
            header_media_url: 'https://cdn.example.com/reports/arjun_trip.pdf',
            client_ref: 'ORDER-002',
          },
        ],
      }}
      responseExample={{
        success: true,
        data: {
          accepted: 2,
          rejected: 0,
          results: [
            { index: 0, success: true, message_id: 'tx_aaa...', status: 'queued', client_ref: 'ORDER-001' },
            { index: 1, success: true, message_id: 'tx_bbb...', status: 'queued', client_ref: 'ORDER-002' },
          ],
        },
      }}
      errorExample={{
        data: {
          accepted: 1,
          rejected: 1,
          results: [
            { index: 0, success: true, message_id: 'tx_aaa...' },
            { index: 1, success: false, error: { code: 'INVALID_MEDIA_URL', message: '...' } },
          ],
        },
      }}
      notes="Each message in results has its own success flag. Failed messages include an error object. Accepted messages are queued independently — one failure does not block the others."
    />

    {/* ── 3. Check Single Status ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>3 · Check Message Status</Title>

    <EndpointCard
      method="GET"
      path="/messages/whatsapp/:message_id"
      scope="messages:read"
      description="Fetch current status, full delivery event timeline, and provider (Meta) response for a transactional message."
      responseExample={{
        success: true,
        data: {
          message_id: 'tx_2cebb94fa5f0b0135b8c097d74760157',
          client_ref: 'ORDER-99001',
          status: 'delivered',
          to_masked: '+91*****0ac',
          to_country_code: '91',
          template_name: 'report_summary',
          wamid: 'wamid.HBgMO...',
          events: [
            { status: 'queued', at: '2026-04-27T10:21:23.863Z' },
            { status: 'sent', at: '2026-04-27T10:21:24.900Z' },
            { status: 'delivered', at: '2026-04-27T10:21:31.200Z' },
          ],
          error: null,
          created_at: '2026-04-27T10:21:23.863Z',
          sent_at: '2026-04-27T10:21:24.900Z',
          delivered_at: '2026-04-27T10:21:31.200Z',
          read_at: null,
          failed_at: null,
        },
      }}
    />

    {/* Status lifecycle */}
    <div style={{ marginBottom: 16 }}>
      <Text strong style={{ fontSize: 13 }}>Message status lifecycle: </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>queued → sent → delivered → read &nbsp;|&nbsp; failed (if Meta rejects)</Text>
      <Table
        dataSource={[
          { status: 'queued', meaning: 'Accepted by MessageOS — waiting for dispatch to Meta.' },
          { status: 'sent', meaning: 'Successfully submitted to Meta WhatsApp API.' },
          { status: 'delivered', meaning: 'Delivered to the recipient\'s device.' },
          { status: 'read', meaning: 'Recipient opened the message.' },
          { status: 'failed', meaning: 'Meta rejected the message — check error.code and error.message in the response.' },
        ]}
        columns={[
          { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Text code>{v}</Text>, width: 100 },
          { title: 'Meaning', dataIndex: 'meaning', key: 'meaning' },
        ]}
        pagination={false}
        size="small"
        rowKey="status"
        style={{ marginTop: 8, borderRadius: 8 }}
      />
    </div>

    {/* ── 4. Batch Status ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>4 · Batch Status Check (up to 200 IDs)</Title>

    <EndpointCard
      method="POST"
      path="/messages/whatsapp/status/batch"
      scope="messages:read"
      description="Check status for up to 200 message IDs in one call."
      requestExample={{
        message_ids: [
          'tx_2cebb94fa5f0b0135b8c097d74760157',
          'tx_788dbfbef8d4787b92292b8f5e352724',
        ],
      }}
      responseExample={{
        success: true,
        data: {
          count: 2,
          results: [
            {
              message_id: 'tx_2cebb94fa5f0b0135b8c097d74760157',
              status: 'delivered',
              wamid: 'wamid.HBgMO...',
              error_code: null,
              error_message: null,
              created_at: '2026-04-27T10:21:23.863Z',
              sent_at: '2026-04-27T10:21:24.900Z',
              delivered_at: '2026-04-27T10:21:31.200Z',
              read_at: null,
              failed_at: null,
            },
            {
              message_id: 'tx_788dbfbef8d4787b92292b8f5e352724',
              status: 'sent',
              wamid: 'wamid.HBgMO...',
              error_code: null,
              error_message: null,
              created_at: '2026-04-27T10:21:25.000Z',
              sent_at: '2026-04-27T10:21:26.100Z',
              delivered_at: null,
              read_at: null,
              failed_at: null,
            },
          ],
        },
      }}
    />

    {/* ── 5. List with Filters ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>5 · List &amp; Filter Messages</Title>

    <EndpointCard
      method="GET"
      path="/messages/whatsapp"
      scope="messages:read"
      description="Paginated, filterable log of all transactional sends for your account."
      queryTable={[
        { field: 'status', type: 'string', default: '—', description: 'Filter by status: queued | sent | delivered | read | failed' },
        { field: 'template_name', type: 'string', default: '—', description: 'Filter by template name.' },
        { field: 'from_date', type: 'ISO date', default: '—', description: 'Start of date range (inclusive).' },
        { field: 'to_date', type: 'ISO date', default: '—', description: 'End of date range (inclusive).' },
        { field: 'limit', type: 'integer', default: '50', description: 'Max results per page (max 200).' },
        { field: 'offset', type: 'integer', default: '0', description: 'Pagination offset.' },
      ]}
      responseExample={{
        success: true,
        data: [
          {
            message_id: 'tx_2cebb94fa5f0b0135b8c097d74760157',
            status: 'delivered',
            to_country_code: '91',
            template_name: 'report_summary',
            client_ref: 'ORDER-001',
            accepted_at: '2026-04-27T10:21:23.863Z',
          },
        ],
        meta: { count: 142 },
      }}
    />

    <EndpointCard
      method="GET"
      path="/messages/whatsapp/search"
      scope="messages:read"
      description="Search messages by to_hash, client_ref, or Meta wamid. At least one filter is required."
      queryTable={[
        { field: 'to_hash', type: 'string', default: '—', description: 'SHA-256 hash of recipient phone (returned from /send response).' },
        { field: 'client_ref', type: 'string', default: '—', description: 'Your own order / reference ID.' },
        { field: 'wamid', type: 'string', default: '—', description: "Meta's message ID." },
        { field: 'limit', type: 'integer', default: '50', description: 'Max 200.' },
      ]}
      responseExample={{
        success: true,
        data: [
          {
            message_id: 'tx_2cebb94fa5f0b0135b8c097d74760157',
            status: 'delivered',
            client_ref: 'ORDER-99001',
            template_name: 'report_summary',
            accepted_at: '2026-04-27T10:21:23.863Z',
          },
        ],
      }}
    />

    {/* ── 6. Analytics ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>6 · Analytics</Title>

    <EndpointCard
      method="GET"
      path="/messages/whatsapp/analytics/overview"
      scope="messages:read"
      description="Delivery stats for your account — defaults to the last 30 days. Returns one row per status."
      queryTable={[
        { field: 'from_date', type: 'ISO date', default: '30 days ago', description: 'Start date for the analytics window.' },
        { field: 'to_date', type: 'ISO date', default: 'today', description: 'End date for the analytics window.' },
      ]}
      responseExample={{
        success: true,
        data: [
          { status: 'sent', count: 1842 },
          { status: 'delivered', count: 1789 },
          { status: 'read', count: 934 },
          { status: 'failed', count: 53 },
        ],
        meta: {
          from_date: '2026-04-01T00:00:00.000Z',
          to_date: '2026-04-30T23:59:59.000Z',
        },
      }}
    />

    <EndpointCard
      method="GET"
      path="/messages/whatsapp/analytics/by-template"
      scope="messages:read"
      description="Per-template breakdown — one row per (template_name, status) pair."
      queryTable={[
        { field: 'from_date', type: 'ISO date', default: '30 days ago', description: 'Start date.' },
        { field: 'to_date', type: 'ISO date', default: 'today', description: 'End date.' },
      ]}
      responseExample={{
        success: true,
        data: [
          { template_name: 'order_confirmed', status: 'delivered', count: 888 },
          { template_name: 'order_confirmed', status: 'failed', count: 30 },
          { template_name: 'report_summary', status: 'delivered', count: 901 },
          { template_name: 'report_summary', status: 'failed', count: 23 },
        ],
        meta: {
          from_date: '2026-04-01T00:00:00.000Z',
          to_date: '2026-04-30T23:59:59.000Z',
        },
      }}
    />

    {/* ── 7. Error Codes ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>7 · Error Codes</Title>

    <div style={{ marginBottom: 16 }}>
      <Table
        dataSource={[
          { code: 'MISSING_TO', cause: '`to` field missing or empty', fix: 'Add "to": "+91..."' },
          { code: 'MISSING_TEMPLATE', cause: 'No template.name or template_id provided', fix: 'Add "template": { "name": "..." }' },
          { code: 'TEMPLATE_NOT_FOUND', cause: 'Template name/ID not found in database', fix: 'Verify template exists and belongs to your account' },
          { code: 'TEMPLATE_NOT_APPROVED', cause: 'Template status is not "approved"', fix: 'Wait for Meta approval (5–30 min) or use a different template' },
          { code: 'TEMPLATE_FORBIDDEN', cause: 'Template belongs to another company', fix: 'Use templates from your own account' },
          { code: 'INVALID_PHONE', cause: 'Phone number cannot be parsed', fix: 'Use full E.164 format: +919172768730' },
          { code: 'INVALID_MEDIA_URL', cause: 'URL is not https:// (local path, http://, private IP)', fix: 'Upload file to S3/CDN and pass an https:// URL' },
          { code: 'INVALID_BODY', cause: 'Request body is missing or not valid JSON', fix: 'Ensure Content-Type: application/json' },
          { code: 'PLAN_LIMIT_EXCEEDED', cause: 'Monthly transactional quota reached', fix: 'Upgrade plan or wait for renewal' },
        ]}
        columns={[
          { title: 'Error Code', dataIndex: 'code', key: 'code', render: (v) => <Text code>{v}</Text>, width: 220 },
          { title: 'Cause', dataIndex: 'cause', key: 'cause', width: 280 },
          { title: 'Fix', dataIndex: 'fix', key: 'fix' },
        ]}
        pagination={false}
        size="small"
        rowKey="code"
        style={{ marginTop: 8, borderRadius: 8 }}
      />
    </div>

    {/* ── 8. Webhooks ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>8 · Delivery Webhooks (Callbacks)</Title>

    <Alert
      type="info"
      showIcon
      message="Set callback_url to receive real-time status events"
      description={(
        <div>
          <p style={{ marginBottom: 8 }}>When you include <Text code>callback_url</Text> in your send request, MessageOS POSTs a JSON event to that URL for every status change. Your endpoint must return <b>HTTP 200</b> within 10 seconds.</p>
          <Text strong>Event payloads:</Text>
          <CodeBlock>{`// message.queued
{
  "event": "message.queued",
  "sent_at": "2026-04-27T10:21:23.972Z",
  "data": {
    "message_id": "tx_2cebb94fa5f0b0135b8c097d74760157",
    "client_ref": "ORDER-99001",
    "template_id": "38398f0c-1ffb-4681-83f7-d1a18021cf86",
    "template_name": "report_summary"
  }
}

// message.sent
{
  "event": "message.sent",
  "data": {
    "message_id": "tx_2cebb94fa5f0b0135b8c097d74760157",
    "client_ref": "ORDER-99001",
    "wamid": "wamid.HBgMO..."
  }
}

// message.delivered
{
  "event": "message.delivered",
  "data": {
    "message_id": "tx_2cebb94fa5f0b0135b8c097d74760157",
    "client_ref": "ORDER-99001",
    "delivered_at": "2026-04-27T10:21:31.200Z"
  }
}

// message.failed
{
  "event": "message.failed",
  "data": {
    "message_id": "tx_2cebb94fa5f0b0135b8c097d74760157",
    "client_ref": "ORDER-99001",
    "template_name": "report_summary",
    "error": {
      "code": "100",
      "message": "(#100) Param template.components.parameters.document.link is not a valid URI."
    }
  }
}`}</CodeBlock>
          <Text type="secondary">Possible event names: <Text code>message.queued</Text> · <Text code>message.sent</Text> · <Text code>message.delivered</Text> · <Text code>message.read</Text> · <Text code>message.failed</Text></Text>
        </div>
      )}
      style={{ marginBottom: 16, borderRadius: 8 }}
    />

    {/* ── 9. Inbox / Reply Tracking ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>9 · Inbox &amp; Reply Tracking</Title>

    <Paragraph type="secondary" style={{ marginBottom: 8 }}>
      When a user replies to your transactional message, the reply is stored and queryable. Auth uses the same <Text code>x-api-key</Text> header — no JWT needed. Raw phone numbers are never returned; use the <Text code>to_hash</Text> from your send response.
    </Paragraph>

    <EndpointCard
      method="GET"
      path="/inbox/external/messages"
      scope="messages:read"
      description="List the conversation thread for a recipient — outbound sends and any inbound replies. Filter by to_hash or client_ref."
      queryTable={[
        { field: 'to_hash', type: 'string', default: '—', description: 'SHA-256 hash of recipient phone (returned from /send response).' },
        { field: 'client_ref', type: 'string', default: '—', description: 'Your own order / reference ID.' },
        { field: 'source', type: 'string', default: '—', description: 'Filter by source: transactional_api or byo_webhook.' },
        { field: 'limit', type: 'integer', default: '50', description: 'Max 200.' },
        { field: 'offset', type: 'integer', default: '0', description: 'Pagination offset.' },
      ]}
      responseExample={{
        success: true,
        data: [
          {
            msg_id: 'ext_001',
            to_hash_short: '7735bce7…',
            client_ref: 'ORDER-99001',
            display_name: 'H*** S***',
            preview: 'Dear Haris, Report for MH30OR6665 (Nagpur)…',
            direction: 'outbound',
            source: 'transactional_api',
            occurred_at: '2026-04-27T10:21:23.863Z',
            purge_at: '2026-07-27T10:21:23.863Z',
          },
          {
            msg_id: 'ext_002',
            to_hash_short: '7735bce7…',
            client_ref: null,
            display_name: 'H*** S***',
            preview: 'Thank you, received the report',
            direction: 'inbound',
            source: 'transactional_api',
            occurred_at: '2026-04-27T10:25:10.000Z',
            purge_at: '2026-07-27T10:25:10.000Z',
          },
        ],
        meta: { count: 2 },
      }}
      notes="Privacy: raw phone numbers are never returned. display_name has all characters after the first masked (H*** S***). purge_at is 90 days after occurred_at."
    />

    <EndpointCard
      method="GET"
      path="/inbox/external/messages/:msg_id"
      scope="messages:read"
      description="Get a single inbox message by its msg_id."
      responseExample={{
        success: true,
        data: {
          msg_id: 'ext_001',
          direction: 'outbound',
          source: 'transactional_api',
          preview: 'Dear Haris, Report for MH30OR6665…',
          client_ref: 'ORDER-99001',
          occurred_at: '2026-04-27T10:21:23.863Z',
        },
      }}
    />

    {/* ── 10. Common Mistakes ── */}
    <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>10 · Common Mistakes &amp; Fixes</Title>

    <Table
      dataSource={[
        {
          key: 1,
          mistake: 'Local file path as header_media_url',
          example: '"/home/user/Desktop/report.pdf"',
          fix: 'Upload to S3 or any CDN first, then use the https:// URL.',
        },
        {
          key: 2,
          mistake: 'http:// instead of https://',
          example: '"http://myserver.com/report.pdf"',
          fix: 'Enable HTTPS on your server (free via Let\'s Encrypt) or use S3.',
        },
        {
          key: 3,
          mistake: 'Phone without country code',
          example: '"to": "9172768730"',
          fix: 'Always include + and country code: "+919172768730".',
        },
        {
          key: 4,
          mistake: 'Template not approved by Meta',
          example: 'error: TEMPLATE_NOT_APPROVED',
          fix: 'Check the Templates section in the dashboard. Approval takes 5–30 minutes.',
        },
        {
          key: 5,
          mistake: 'Wrong variable count',
          example: 'Template has {{1}}, {{2}}, {{3}} but only "1" and "2" passed',
          fix: 'Count all {{N}} placeholders and provide all of them in variables.',
        },
        {
          key: 6,
          mistake: 'Template name mismatch (case/spaces)',
          example: '"name": "Report Summary"',
          fix: 'Template names are lowercase with underscores: "report_summary".',
        },
        {
          key: 7,
          mistake: 'Pre-signed URL expires too quickly',
          example: 'S3 URL with 60-second expiry — Meta fetch fails',
          fix: 'Set pre-signed URL expiry to at least 1 hour (3600s). Recommended: 86400 (24h).',
        },
      ]}
      columns={[
        { title: 'Mistake', dataIndex: 'mistake', key: 'mistake', width: 220 },
        { title: 'Example', dataIndex: 'example', key: 'example', width: 260, render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
        { title: 'Fix', dataIndex: 'fix', key: 'fix' },
      ]}
      pagination={false}
      size="small"
      rowKey="key"
      style={{ marginBottom: 16, borderRadius: 8 }}
    />

    {/* ── Rate Limits ── */}
    <Alert
      type="warning"
      showIcon
      message="Quota &amp; rate limits"
      description="Transactional sends count toward your plan's whatsapp_transactional_limit. Hard limit: 80 req/sec per account. Bursts above the limit return HTTP 429. Bulk sends count each message in the array individually. For 1000+ users, batch into groups of ≤ 100."
      style={{ borderRadius: 8 }}
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB — WhatsApp BYO Audience Campaigns (Phase 3 / §9)
   ═════════════════════════════════════════════════════════════ */

const WhatsAppByoCampaignsTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>BYO Audience Presets</Title>
      <CopyPromptButton promptKey="wa-byo-campaigns" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary">
      Define reusable <Text strong>audience presets</Text> — a webhook configuration that fetches contacts from your own server when a campaign runs. Create a preset once and reuse it across many campaigns.
    </Paragraph>

    <Alert
      type="info"
      showIcon
      message="How Audience Presets Work"
      description={(
        <div>
          <p>1. <strong>You build</strong> an HTTPS endpoint on your server that returns a list of recipients.</p>
          <p>2. Create a preset here with that URL + optional auth token.</p>
          <p>3. At campaign launch, MessageOS <strong>POST</strong>s to your URL, reads the contacts, and sends WhatsApp messages.</p>
          <p>Use <Text code>POST /campaigns/whatsapp/audience/test</Text> to probe your webhook before launch.</p>
        </div>
      )}
      style={{ marginBottom: 20 }}
    />

    {/* ── DEVELOPER GUIDE ───────────────────────────────────── */}
    <Divider orientation="left">
      <CodeOutlined style={{ marginRight: 6 }} />
      Developer Guide — Building Your Webhook Endpoint
    </Divider>

    <Alert
      type="warning"
      showIcon
      message="You must build and host this endpoint yourself"
      description="MessageOS calls your server when a campaign runs. The endpoint must be publicly reachable over HTTPS (not localhost)."
      style={{ marginBottom: 16 }}
    />

    <Title level={5} style={{ marginBottom: 8 }}>What MessageOS Sends to Your Endpoint</Title>
    <Paragraph type="secondary" style={{ marginBottom: 4 }}>
      MessageOS uses <strong>POST</strong> to call your webhook — no query parameters are added. Auth headers depend on the auth type you configured.
    </Paragraph>
    <Card size="small" style={{ background: '#1a1a2e', marginBottom: 4 }}>
      <pre style={{ color: '#e6edf3', fontSize: 12, margin: 0 }}>{`# auth.type = "none"
POST https://your-server.com/webhooks/messageos/audience
Content-Type: application/json
X-MOS-Signature: t=1714294578,sha256=abc123def456...
X-MOS-Timestamp: 1714294578

Body: {}`}</pre>
    </Card>
    <Card size="small" style={{ background: '#1a1a2e', marginBottom: 4 }}>
      <pre style={{ color: '#e6edf3', fontSize: 12, margin: 0 }}>{`# auth.type = "bearer"
POST https://your-server.com/webhooks/messageos/audience
Content-Type: application/json
Authorization: Bearer <your-token>
X-MOS-Signature: t=1714294578,sha256=abc123def456...
X-MOS-Timestamp: 1714294578

Body: {}`}</pre>
    </Card>
    <Card size="small" style={{ background: '#1a1a2e', marginBottom: 16 }}>
      <pre style={{ color: '#e6edf3', fontSize: 12, margin: 0 }}>{`# auth.type = "header"  (e.g. header_name = "X-Audience-Secret")
POST https://your-server.com/webhooks/messageos/audience
Content-Type: application/json
X-Audience-Secret: <your-token>
X-MOS-Signature: t=1714294578,sha256=abc123def456...
X-MOS-Timestamp: 1714294578

Body: {}`}</pre>
    </Card>

    <Title level={5} style={{ marginBottom: 8 }}>Required Response Format</Title>
    <Paragraph type="secondary" style={{ marginBottom: 4 }}>
      Your endpoint <strong>must</strong> return JSON with a <Text code>data</Text> array. Each object needs a <Text code>mobile</Text> field (country code + number, no <Text code>+</Text> prefix).
    </Paragraph>
    <Card size="small" style={{ background: '#1a1a2e', marginBottom: 8 }}>
      <pre style={{ color: '#e6edf3', fontSize: 12, margin: 0 }}>{`{
  "success": true,
  "count": 3,
  "data": [
    { "mobile": "917038689989", "full_name": "Rahul Sharma" },
    { "mobile": "919172896771", "full_name": "Priya Singh" },
    { "mobile": "918412963746", "full_name": "Amit Verma" }
  ]
}`}</pre>
    </Card>
    <Table
      size="small"
      pagination={false}
      style={{ marginBottom: 20 }}
      columns={[
        { title: 'Field', dataIndex: 'field', key: 'f', width: 140, render: v => <Text code>{v}</Text> },
        { title: 'Required', dataIndex: 'req', key: 'r', width: 90, render: v => <Tag color={v === 'Yes' ? 'red' : 'default'}>{v}</Tag> },
        { title: 'Format', dataIndex: 'fmt', key: 'fmt' },
      ]}
      dataSource={[
        { key: 1, field: 'data', req: 'Yes', fmt: 'Array of contact objects' },
        { key: 2, field: 'mobile', req: 'Yes (per record)', fmt: 'Country code + number, no + prefix — e.g. "917038689989"' },
        { key: 3, field: 'full_name', req: 'No', fmt: 'String — passed as template variable if mapped' },
        { key: 4, field: 'count', req: 'No', fmt: 'Total count of records (informational)' },
        { key: 5, field: 'success', req: 'No', fmt: 'Boolean — any 2xx HTTP status is treated as success' },
      ]}
    />

    <Title level={5} style={{ marginBottom: 8 }}>Verifying the Signature (Recommended)</Title>
    <Paragraph type="secondary" style={{ marginBottom: 4 }}>
      Use the <Text strong>Signing Secret</Text> (shown once after preset creation) to verify every inbound request. Prevents spoofed calls to your endpoint.
    </Paragraph>
    <Card size="small" style={{ background: '#1a1a2e', marginBottom: 8 }}>
      <pre style={{ color: '#e6edf3', fontSize: 12, margin: 0 }}>{`// Node.js / Express
const crypto = require('crypto');

function verifyMOSSignature(req, signingSecret) {
  const sigHeader = req.headers['x-mos-signature'] || '';
  const timestamp  = req.headers['x-mos-timestamp'] || '';
  const rawBody    = JSON.stringify(req.body);          // or req.rawBody if using bodyParser
  const payload    = \`\${timestamp}.\${rawBody}\`;
  const expected   = crypto.createHmac('sha256', signingSecret)
                           .update(payload).digest('hex');
  const received   = sigHeader.split('sha256=')[1];
  if (!received) return false;
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(received));
}

app.post('/webhooks/messageos/audience', (req, res) => {
  if (!verifyMOSSignature(req, process.env.MOS_SIGNING_SECRET)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }
  const contacts = getContactsFromDatabase();   // your logic here
  res.json({ success: true, count: contacts.length, data: contacts });
});`}</pre>
    </Card>
    <Card size="small" style={{ background: '#1a1a2e', marginBottom: 20 }}>
      <pre style={{ color: '#e6edf3', fontSize: 12, margin: 0 }}>{`# Python / FastAPI
import hmac, hashlib, os
from fastapi import FastAPI, Request, HTTPException

app = FastAPI()
SIGNING_SECRET = os.getenv("MOS_SIGNING_SECRET")

@app.post("/webhooks/messageos/audience")
async def audience(request: Request):
    body      = await request.body()
    timestamp = request.headers.get("x-mos-timestamp", "")
    sig_hdr   = request.headers.get("x-mos-signature", "")
    payload   = f"{timestamp}.{body.decode()}"
    expected  = hmac.new(SIGNING_SECRET.encode(), payload.encode(), hashlib.sha256).hexdigest()
    received  = sig_hdr.split("sha256=")[-1]
    if not hmac.compare_digest(expected, received):
        raise HTTPException(status_code=401, detail="Invalid signature")
    contacts = get_contacts()   # your logic here
    return {"success": True, "count": len(contacts), "data": contacts}`}</pre>
    </Card>

    <Divider orientation="left">API Reference</Divider>

    {/* 14.1 Create Preset */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/audience/presets/create"
      scope="campaigns:manage"
      description="Create a new audience preset. auth.token is stored encrypted and sent to your webhook on every call. Returns a one-time signing_secret — store it securely."
      requestExample={{
        name: 'MettaMate Audience',
        url: 'https://your-server.com/webhooks/messageos/audience',
        auth: { type: 'header', header_name: 'X-Audience-Secret', token: 'c753ca5bc4377113...' },
        hard_send_cap: 10000,
      }}
      responseExample={{
        success: true,
        data: {
          preset_id: '7e1557ff-d78b-43c8-821c-e209f9c0c293',
          name: 'MettaMate Audience',
          url: 'https://your-server.com/webhooks/messageos/audience',
          signing_secret: 'whsec_a3MBYnz5-09OjZIoI612inzbzB7zZ4ge',
          created_at: '2026-04-28T12:00:07.030Z',
          _secret_warning: 'Store this signing_secret securely — it will NOT be shown again.',
        },
      }}
      notes="auth.type: 'none' | 'bearer' | 'header' — 'basic' is NOT supported. auth.token is stored encrypted and used for every outbound webhook call. signing_secret shown ONCE — never returned in list/get."
    />

    {/* 14.2 List Presets */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/audience/presets/list"
      scope="campaigns:manage"
      description="List all saved audience presets. auth.token and signing_secret are never returned here."
      requestExample={{ limit: 50, offset: 0 }}
      responseExample={{
        success: true,
        data: [{ preset_id: '7e1557ff-...', name: 'Premium Users', url: 'https://yourapp.com/webhooks/messageos/audience', created_at: '2026-04-28T12:00:07.030Z', last_used_at: null }],
        meta: { count: 1 },
      }}
    />

    {/* 14.3 Update Preset */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/audience/presets/update"
      scope="campaigns:manage"
      description="Update preset fields — only supplied fields are changed. Pass auth.token to update credentials. Pass rotate_secret: true to regenerate the signing secret (shown once in response)."
      requestExample={{ preset_id: '7e1557ff-d78b-43c8-821c-e209f9c0c293', name: 'Updated Name', auth: { type: 'bearer', token: 'new-token-here' } }}
      responseExample={{ success: true, data: { preset_id: '7e1557ff-...', name: 'Updated Name', updated_at: '2026-04-28T13:00:00.000Z' } }}
      notes="auth.token is stored encrypted and used for outbound calls. To rotate signing secret: { preset_id: '...', rotate_secret: true } — response includes new signing_secret shown once. 'basic' auth type is not supported."
    />

    {/* 14.4 Delete Preset */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/audience/presets/delete"
      scope="campaigns:manage"
      description="Delete an audience preset"
      requestExample={{ preset_id: '7e1557ff-d78b-43c8-821c-e209f9c0c293' }}
      responseExample={{ success: true, data: { preset_id: '7e1557ff-...', deleted: true } }}
    />

    {/* 14.5 Test Webhook Probe */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/audience/test"
      scope="campaigns:manage"
      description="Fire a live test probe to the preset's webhook URL and validate the response schema. Backend loads auth automatically from the preset."
      requestExample={{ url: 'https://yourapp.com/webhooks/messageos/audience', preset_id: '7e1557ff-d78b-43c8-821c-e209f9c0c293' }}
      responseExample={{
        success: true,
        data: { http_status: 200, latency_ms: 312, data_count: 3, data_array_present: true, sample_body_preview: { success: true, count: 3, data: [{ mobile: '917038689989', full_name: 'Rahul Sharma' }] } },
      }}
      notes="MessageOS POSTs to your webhook URL — no query params are added. If preset_id is provided, auth is loaded server-side automatically (do NOT send auth.token from the browser). For testing a new unsaved URL: { url, auth: { type, header_name } } — omit token. 'basic' auth type is not supported."
    />

    {/* 14.6 Preview Contract */}
    <EndpointCard
      method="POST"
      path="/campaigns/whatsapp/preview-contract"
      scope="campaigns:manage"
      description="Dry-run the audience fetch and validate phone numbers — does NOT send any messages"
      requestExample={{ preset_id: '7e1557ff-d78b-43c8-821c-e209f9c0c293' }}
      responseExample={{
        success: true,
        data: { total_records: 1500, valid_phones: 1432, invalid_phones: 68, sample: [{ phone: '919876543210', name: 'Rahul Sharma' }] },
      }}
      notes="invalid_phones are those missing country code, too short/long, or non-numeric. Use this before scheduling a campaign."
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB — Webhooks (Phase 3 / §9)
   ═════════════════════════════════════════════════════════════ */

const WebhooksTab = ({ isEnterprise }) => (
  <div>
    <div className="tab-header-row">
      <Title level={4} style={{ margin: 0 }}>Webhooks</Title>
      <CopyPromptButton promptKey="webhooks" label="Copy UI Prompt" isEnterprise={isEnterprise} />
    </div>
    <Paragraph type="secondary">
      Subscribe to real-time delivery, status and inbound-reply events. Base path: <Text code>/api/v1/external/webhooks</Text> — Scope: <Tag color="blue">webhooks:manage</Tag>
    </Paragraph>

    {/* Supported Events Table */}
    <Title level={5} style={{ marginTop: 16 }}>Supported Event Names</Title>
    <Table
      size="small"
      pagination={false}
      rowKey="event"
      dataSource={[
        { event: 'message.queued', desc: 'Message accepted and queued for dispatch' },
        { event: 'message.sent', desc: 'Message sent to Meta / carrier' },
        { event: 'message.delivered', desc: 'Delivery confirmed by WhatsApp' },
        { event: 'message.read', desc: 'Message read by recipient' },
        { event: 'message.failed', desc: 'Dispatch failed (see error details in payload)' },
        { event: 'message.received', desc: 'Inbound reply received from a contact' },
        { event: 'campaign.audience.fetch.started', desc: 'Campaign audience pull began' },
        { event: 'campaign.audience.fetch.completed', desc: 'Audience pull finished' },
        { event: 'campaign.audience.fetch.failed', desc: 'Audience pull failed' },
        { event: 'campaign.completed', desc: 'Campaign fully dispatched' },
        { event: 'campaign.refunded', desc: 'Campaign refunded' },
      ]}
      columns={[
        { title: 'Event', dataIndex: 'event', render: (v) => <Text code>{v}</Text>, width: 260 },
        { title: 'Description', dataIndex: 'desc' },
      ]}
      style={{ marginBottom: 24 }}
    />

    {/* Signature Verification */}
    <Alert
      type="info"
      showIcon
      message="Signature Verification"
      description={(
        <div>
          Every delivery includes the header <Text code>X-MessageOS-Signature: sha256=&lt;hex&gt;</Text>. Verify with your subscription secret:
          <CodeBlock>{`// Node.js
const crypto = require('crypto');
function verifySignature(rawBody, signature, secret) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(rawBody)   // raw Buffer, NOT parsed JSON
    .digest('hex');
  return crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected));
}

# Python
import hmac, hashlib
def verify_signature(raw_body: bytes, signature: str, secret: str) -> bool:
    expected = 'sha256=' + hmac.new(secret.encode(), raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(signature, expected)`}</CodeBlock>
        </div>
      )}
      style={{ marginBottom: 24 }}
    />

    <Divider />
    <Title level={5}>Subscriptions — <Text code>/api/v1/external/webhooks/subscriptions</Text></Title>

    {/* 4.1 Create Subscription */}
    <EndpointCard
      method="POST"
      path="/webhooks/subscriptions"
      scope="webhooks:manage"
      description="Create a webhook subscription. The secret is returned ONCE — store it securely."
      requestExample={{
        url: 'https://your-server.com/webhook',
        events: ['message.delivered', 'message.failed', 'message.read'],
        description: 'Production status webhook',
        secret: 'your-own-secret-min-16-chars',
      }}
      responseExample={{
        success: true,
        data: {
          subscription_id: 'whs_01HXZ...',
          url: 'https://your-server.com/webhook',
          events: ['message.delivered', 'message.failed', 'message.read'],
          is_active: true,
          created_at: '2025-01-01T12:00:00.000Z',
          secret: 'whsec_abc123...',
          _secret_warning: 'Store securely — secret will not be shown again.',
        },
      }}
      notes="secret is only returned on creation (and on rotate_secret). NOT returned on list/get."
    />

    {/* 4.2 List Subscriptions */}
    <EndpointCard
      method="GET"
      path="/webhooks/subscriptions"
      scope="webhooks:manage"
      description="List all webhook subscriptions"
      responseExample={{
        success: true,
        data: [{ subscription_id: 'whs_01HXZ...', url: 'https://your-server.com/webhook', events: ['message.delivered', 'message.failed'], is_active: true, created_at: '2025-01-01T12:00:00.000Z' }],
      }}
    />

    {/* 4.3 Get Subscription by ID */}
    <EndpointCard
      method="GET"
      path="/webhooks/subscriptions/:id"
      scope="webhooks:manage"
      description="Get a single subscription by ID"
      responseExample={{
        success: true,
        data: { subscription_id: 'whs_01HXZ...', url: 'https://your-server.com/webhook', events: ['message.delivered'], is_active: true },
      }}
    />

    {/* 4.4 Update Subscription */}
    <EndpointCard
      method="PATCH"
      path="/webhooks/subscriptions/:id"
      scope="webhooks:manage"
      description="Update url, events, description, active state. Set rotate_secret: true to get a new HMAC secret."
      requestExample={{ url: 'https://new-server.com/webhook', events: ['message.delivered', 'message.read', 'message.failed'], is_active: true, rotate_secret: true }}
      responseExample={{ success: true, data: { subscription_id: 'whs_01HXZ...', url: 'https://new-server.com/webhook', is_active: true, secret: 'whsec_newSecret...' } }}
      notes="secret is only returned in response when rotate_secret: true was sent."
    />

    {/* 4.5 Delete Subscription */}
    <EndpointCard
      method="DELETE"
      path="/webhooks/subscriptions/:id"
      scope="webhooks:manage"
      description="Permanently delete a subscription"
      responseExample={{ success: true, data: { subscription_id: 'whs_01HXZ...', deleted: true } }}
    />

    {/* 4.6 Test Subscription */}
    <EndpointCard
      method="POST"
      path="/webhooks/subscriptions/:id/test"
      scope="webhooks:manage"
      description="Fire a webhook.test event to your URL immediately (no request body needed)"
      responseExample={{ success: true, data: { delivery_id: 'whd_01HXZ...', status: 'pending', queued: true } }}
      notes={`Your endpoint will receive: { "event": "webhook.test", "message": "This is a test webhook from MessageOS.", "sent_at": "..." }`}
    />

    <Divider />
    <Title level={5}>Webhook Deliveries — <Text code>/api/v1/external/webhooks/deliveries</Text></Title>

    {/* 5.1 List Deliveries */}
    <EndpointCard
      method="GET"
      path="/webhooks/deliveries"
      scope="webhooks:manage"
      description="List delivery history — filterable by subscription, status"
      queryTable={[
        { field: 'subscription_id', type: 'string', default: '—', description: 'Filter by subscription' },
        { field: 'status', type: 'string', default: '—', description: 'pending / delivered / failed' },
        { field: 'limit', type: 'number', default: '50', description: 'Max 200' },
        { field: 'offset', type: 'number', default: '0', description: 'Pagination offset' },
      ]}
      responseExample={{
        success: true,
        data: [{ delivery_id: 'whd_01HXZ...', subscription_id: 'whs_01HXZ...', event: 'message.delivered', status: 'delivered', http_status: 200, attempts: 1, last_attempt_at: '2025-01-01T12:00:05.000Z', created_at: '2025-01-01T12:00:00.000Z' }],
        meta: { count: 1 },
      }}
    />

    {/* 5.2 Get Delivery by ID */}
    <EndpointCard
      method="GET"
      path="/webhooks/deliveries/:id"
      scope="webhooks:manage"
      description="Get a single delivery with full payload and response body"
      responseExample={{
        success: true,
        data: {
          delivery_id: 'whd_01HXZ...', event: 'message.failed',
          payload: { event: 'message.failed', message_id: 'tx_abc...', error: { code: '131047', message: 'Re-engagement message' } },
          status: 'failed', http_status: 500, attempts: 3, response_body: 'Internal Server Error',
          created_at: '2025-01-01T12:00:00.000Z',
        },
      }}
    />

    {/* 5.3 Redrive (Retry) */}
    <EndpointCard
      method="POST"
      path="/webhooks/deliveries/:id/redrive"
      scope="webhooks:manage"
      description="Re-queue a failed or abandoned delivery for immediate retry (no request body needed)"
      responseExample={{ success: true, data: { delivery_id: 'whd_01HXZ...', status: 'pending', attempts: 3 } }}
      notes="Only deliveries in failed or abandoned state can be redriven. Returns NOT_REDRIVABLE otherwise."
    />

    <Divider />
    <Title level={5}>Webhook Event Payload Examples</Title>

    <EndpointCard
      method="PAYLOAD"
      path="message.delivered"
      scope=""
      description="Delivered to your endpoint when a message is confirmed delivered"
      requestExample={{ event: 'message.delivered', company_id: '557df7a1-...', message_id: 'tx_3f2504e...', client_ref: 'order_12345', wamid: 'wamid.HBgLNjE5...', delivered_at: '2025-01-01T12:00:10.000Z' }}
    />
    <EndpointCard
      method="PAYLOAD"
      path="message.failed"
      scope=""
      description="Delivered when a message fails to send"
      requestExample={{ event: 'message.failed', company_id: '557df7a1-...', message_id: 'tx_3f2504e...', client_ref: 'order_12345', error: { code: '131047', message: 'Re-engagement message — user must initiate conversation first' }, failed_at: '2025-01-01T12:00:05.000Z' }}
    />
    <EndpointCard
      method="PAYLOAD"
      path="message.received (inbound reply)"
      scope=""
      description="Delivered when a recipient replies to a message"
      requestExample={{ event: 'message.received', company_id: '557df7a1-...', from_hash: 'a1b2c3d4...', client_ref: 'order_12345', preview: 'When will it arrive?', occurred_at: '2025-01-01T12:15:00.000Z' }}
      notes="Phone numbers are never returned — only from_hash for privacy."
    />
    <EndpointCard
      method="PAYLOAD"
      path="campaign.completed"
      scope=""
      description="Delivered when all campaign messages have been dispatched"
      requestExample={{ event: 'campaign.completed', company_id: '557df7a1-...', campaign_id: 'campaign-uuid', campaign_name: 'January Sale', total_sent: 1200, completed_at: '2025-01-01T14:00:00.000Z' }}
    />
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TAB 8 — Errors & Scopes Reference
   ═════════════════════════════════════════════════════════════ */

const ErrorsScopesTab = () => {
  const errorCodes = [
    // Auth errors
    { code: 'MISSING_API_KEY', http: 401, category: 'Auth', meaning: 'x-api-key header not provided' },
    { code: 'INVALID_API_KEY', http: 401, category: 'Auth', meaning: 'API key not found or revoked' },
    { code: 'FORBIDDEN_SCOPE', http: 403, category: 'Auth', meaning: 'API key does not have the required scope' },
    { code: 'PLAN_FEATURE_DISABLED', http: 403, category: 'Auth', meaning: 'Feature disabled on current plan' },
    { code: 'RATE_LIMIT_EXCEEDED', http: 429, category: 'Auth', meaning: 'Per-key limit exceeded (60 req/min default)' },
    // Message errors
    { code: 'MISSING_TO', http: 400, category: 'Message', meaning: 'to (phone number) is required' },
    { code: 'MISSING_TEMPLATE', http: 400, category: 'Message', meaning: 'templateid or templatename not provided' },
    { code: 'INVALID_PHONE', http: 400, category: 'Message', meaning: 'Phone number format invalid or not E.164' },
    { code: 'INVALID_MEDIA_URL', http: 400, category: 'Message', meaning: 'Header media URL is not a valid https:// URL' },
    { code: 'MEDIA_URL_UNREACHABLE', http: 400, category: 'Message', meaning: 'Media URL returned non-200 or timed out' },
    { code: 'TEMPLATE_NOT_FOUND', http: 400, category: 'Message', meaning: 'Template does not exist for this company' },
    { code: 'TEMPLATE_NOT_APPROVED', http: 400, category: 'Message', meaning: 'Template is not in APPROVED state' },
    { code: 'TEMPLATE_FORBIDDEN', http: 400, category: 'Message', meaning: 'Template category/type not permitted for this send' },
    { code: 'BULK_TOO_LARGE', http: 400, category: 'Message', meaning: 'Bulk array exceeds max allowed (1000 per request)' },
    { code: 'EMPTY_BULK', http: 400, category: 'Message', meaning: 'Bulk messages array is empty' },
    // Webhook errors
    { code: 'MISSING_URL', http: 400, category: 'Webhook', meaning: 'url is required for subscription creation' },
    { code: 'INVALID_URL', http: 400, category: 'Webhook', meaning: 'url must be a valid https:// URL' },
    { code: 'MISSING_EVENTS', http: 400, category: 'Webhook', meaning: 'events array must not be empty' },
    { code: 'UNKNOWN_EVENTS', http: 400, category: 'Webhook', meaning: 'One or more event names are not in the supported list' },
    { code: 'NOT_FOUND', http: 404, category: 'Webhook', meaning: 'Subscription or delivery not found' },
    { code: 'NOT_REDRIVABLE', http: 400, category: 'Webhook', meaning: 'Delivery is not in a failed/abandoned state' },
    // Contact errors
    { code: 'BULK_INSERT_ERROR', http: 400, category: 'Contact', meaning: 'One or more contacts in bulk add failed validation' },
    // General errors
    { code: 'VALIDATION_ERROR', http: 400, category: 'General', meaning: 'Missing or invalid fields in the request' },
    { code: 'API_KEY_EXPIRED', http: 401, category: 'Auth', meaning: 'API key has expired' },
    { code: 'API_KEY_REVOKED', http: 401, category: 'Auth', meaning: 'API key was revoked' },
    { code: 'NO_ACTIVE_PLAN', http: 403, category: 'Auth', meaning: 'Company has no active subscription' },
    { code: 'PLAN_NO_API_ACCESS', http: 403, category: 'Auth', meaning: 'Current plan excludes API access' },
    { code: 'IP_NOT_ALLOWED', http: 403, category: 'Auth', meaning: 'Client IP not in allowed_ips whitelist' },
    { code: 'WABA_NOT_CONFIGURED', http: 403, category: 'Auth', meaning: 'WhatsApp Business Account not set up (WhatsApp endpoints only)' },
    { code: 'INTERNAL_ERROR', http: 500, category: 'General', meaning: 'Internal server error — contact support' },
  ];

  const scopes = [
    { scope: 'contacts:read', description: 'Read contacts, addressbooks, labels' },
    { scope: 'contacts:write', description: 'Create, update, delete contacts and addressbooks' },
    { scope: 'templates:read', description: 'Read WhatsApp templates' },
    { scope: 'templates:write', description: 'Create, update, delete WhatsApp templates' },
    { scope: 'email_templates:read', description: 'Read email templates' },
    { scope: 'email_templates:write', description: 'Create, update, delete email templates' },
    { scope: 'messages:send', description: 'Send transactional WhatsApp messages' },
    { scope: 'messages:read', description: 'Read message logs, delivery status, analytics' },
    { scope: 'campaigns:read', description: 'Read WhatsApp campaigns, stats, analytics' },
    { scope: 'campaigns:write', description: 'Create, update, delete WhatsApp campaigns' },
    { scope: 'campaigns:execute', description: 'Send / start WhatsApp campaigns' },
    { scope: 'campaigns:manage', description: 'Manage audience presets and BYO webhook configurations' },
    { scope: 'webhooks:manage', description: 'Create / update / delete / test webhook subscriptions and redrive deliveries' },
    { scope: 'email_campaigns:read', description: 'Read email campaigns, analytics, recipients' },
    { scope: 'email_campaigns:write', description: 'Create, update, delete email campaigns' },
    { scope: 'email_campaigns:execute', description: 'Send/pause/resume email campaigns' },
  ];

  return (
    <div>
      <Title level={4}>Error Codes Reference</Title>
      <Table
        dataSource={errorCodes}
        columns={[
          { title: 'Category', dataIndex: 'category', key: 'category', width: 90, render: (v) => <Tag>{v}</Tag> },
          { title: 'Code', dataIndex: 'code', key: 'code', render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
          { title: 'HTTP', dataIndex: 'http', key: 'http', width: 70, render: (v) => <Tag color={v < 400 ? 'green' : v < 500 ? 'orange' : 'red'}>{v}</Tag> },
          { title: 'Meaning', dataIndex: 'meaning', key: 'meaning' },
        ]}
        pagination={false}
        size="small"
        rowKey="code"
      />

      <Title level={5} style={{ marginTop: 24 }}>Standard Error Response Shape</Title>
      <CodeBlock>{`{
  "success": false,
  "error": {
    "code": "ERROR_CODE",
    "message": "Human-readable message"
  },
  "meta": {
    "requestId": "uuid",
    "timestamp": "ISO-8601"
  }
}`}</CodeBlock>

      <Divider />

      <Title level={4}>Scopes Reference</Title>
      <Table
        dataSource={scopes}
        columns={[
          { title: 'Scope', dataIndex: 'scope', key: 'scope', render: (v) => <Tag color="blue">{v}</Tag> },
          { title: 'Description', dataIndex: 'description', key: 'description' },
        ]}
        pagination={false}
        size="small"
        rowKey="scope"
      />

      <Divider />

      <Title level={5}>Rate Limits</Title>
      <ul>
        <li><strong>Default:</strong> 60 requests per minute per API key</li>
        <li><strong>Bulk endpoints:</strong> count as 1 request</li>
        <li><strong>Headers returned:</strong> <Text code>X-RateLimit-Limit</Text>, <Text code>X-RateLimit-Remaining</Text>, <Text code>X-RateLimit-Reset</Text></li>
      </ul>

      <Divider />

      <Title level={5}>WABA Requirement</Title>
      <Alert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        message="WhatsApp Business Account Required"
        description="All WhatsApp Template and WhatsApp Campaign endpoints require your company to have completed Meta Embedded Signup. If WABA is not set up, all WhatsApp routes return WABA_NOT_CONFIGURED error. Email endpoints are NOT affected by this requirement."
        style={{ marginBottom: 16 }}
      />


    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT — Sidebar + Content layout (Meta-dev-docs style)
   ═══════════════════════════════════════════════════════════════ */

const NAV_ITEMS = [
  { key: 'getting-started', label: 'Getting Started', icon: <BookOutlined />, count: null },
  { key: 'api-keys', label: 'API Keys', icon: <KeyOutlined />, count: 7 },
  { key: 'contacts', label: 'Contacts', icon: <TeamOutlined />, count: 9 },
  { key: 'addressbook', label: 'Addressbooks', icon: <ContainerOutlined />, count: 7 },
  { key: 'wa-templates', label: 'WA Templates', icon: <FileTextOutlined />, count: 6 },
  { key: 'wa-campaigns', label: 'WA Campaigns', icon: <SendOutlined />, count: 13 },
  { key: 'wa-transactional', label: 'WA Transactional', icon: <SendOutlined />, count: 8 },
  { key: 'wa-byo-campaigns', label: 'WA BYO Campaigns', icon: <ApiOutlined />, count: 7 },
  { key: 'webhooks', label: 'Webhooks', icon: <ApiOutlined />, count: 9 },
  { key: 'email-templates', label: 'Email Templates', icon: <MailOutlined />, count: 11 },
  { key: 'email-campaigns', label: 'Email Campaigns', icon: <MailOutlined />, count: 15 },
  { key: 'errors-scopes', label: 'Errors & Scopes', icon: <SafetyOutlined />, count: null },
];

const SECTIONS = {
  'getting-started': GettingStartedTab,
  'api-keys': ApiKeyManagementTab,
  'contacts': ContactsTab,
  'addressbook': AddressbookTab,
  'wa-templates': WhatsAppTemplatesTab,
  'wa-campaigns': WhatsAppCampaignsTab,
  'wa-transactional': WhatsAppTransactionalTab,
  'wa-byo-campaigns': WhatsAppByoCampaignsTab,
  'webhooks': WebhooksTab,
  'email-templates': EmailTemplatesTab,
  'email-campaigns': EmailCampaignsTab,
  'errors-scopes': ErrorsScopesTab,
};

export default function ApiDocsPage() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef(null);
  const { onboardingStatus, loadingOnboarding } = useOnboarding();
  const isEnterprise = useMemo(
    () => (onboardingStatus?.plan_name || '').toLowerCase() === 'enterprise',
    [onboardingStatus]
  );

  const handleNavClick = useCallback((key) => {
    setActiveSection(key);
    setMobileNavOpen(false);
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // Close mobile nav on resize to desktop
  useEffect(() => {
    const handleResize = () => { if (window.innerWidth > 900) setMobileNavOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Plan Gate — enterprise only ──────────────────────────
  if (loadingOnboarding) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (!isEnterprise) return <ApiPlanGate />;

  const ActiveComponent = SECTIONS[activeSection] || GettingStartedTab;

  return (
    <div className="api-docs-page">
      {/* Mobile hamburger */}
      <button className="docs-mobile-toggle" onClick={() => setMobileNavOpen(!mobileNavOpen)}>
        {mobileNavOpen ? <CloseOutlined /> : <MenuOutlined />}
        <span>Navigation</span>
      </button>

      {/* Overlay for mobile */}
      {mobileNavOpen && <div className="docs-overlay" onClick={() => setMobileNavOpen(false)} />}

      {/* Left Sidebar */}
      <aside className={`docs-sidebar ${mobileNavOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-icon"><ApiOutlined /></div>
          <div>
            <div className="sidebar-title">External API</div>
            <div className="sidebar-subtitle">REST API v1 Reference</div>
          </div>
        </div>

        <div className="sidebar-section-label">ENDPOINTS</div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              className={`sidebar-nav-item ${activeSection === item.key ? 'active' : ''}`}
              onClick={() => handleNavClick(item.key)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              {item.count != null && <span className={`nav-count ${activeSection === item.key ? 'active' : ''}`}>{item.count}</span>}
              <RightOutlined className="nav-arrow" />
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <span className="sidebar-version">MessageOS API v1.0</span>
        </div>
      </aside>

      {/* Right Content */}
      <main className="docs-content" ref={contentRef}>
        <div className="docs-content-inner">
          <ActiveComponent isEnterprise={isEnterprise} />
        </div>
      </main>
    </div>
  );
}
